/**
 * New multi-track NLE for voiceover videos (web).
 * Gated by nle_mode === 'new'. Legacy TimelineCutStrip remains for legacy mode.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, Play, Pause, SkipBack, Plus, Music, Volume2, Type, Film, Scissors, Shuffle } from 'react-feather';
import {
  FLIPBOOK_INTRO_DURATION_SEC,
  NEW_NLE_PX_PER_SEC,
  SFX_CATALOG,
  buildSfxUrl,
  cutEffectiveSeconds,
  sumCutsSeconds,
  activeCutIndexAtTime,
  buildSubtitleBlocks,
  formatNleTime,
  seedCutsToDuration,
  appendCutsToDuration,
  resolveRequiredSeconds,
  normalizeClientSfxCues,
  timelinePxFromTime,
  timelineTimeFromPx,
  timelineWindowSec,
  insertCutIndexAtPlayhead,
  playheadJustPastMidCut,
  RAPID_SHUTTER_URL,
  cutUsageCounts,
  cutPickerDurationLabel,
  extractFirstSentence,
  firstSentenceEndSeconds,
  firstSentenceTimedWords,
  cutPreviewUrl,
  cutPosterUrl,
  sfxCueDuration,
  stampCutKey,
  stampCutKeys,
  buildCardPrefixOffsets,
} from '../utils/newNle';
import { filterStudioFonts } from '../utils/studioFonts';
import api from '../api';
import NleHlsPreview, { preloadHlsEngine } from './NleHlsPreview';
import NleProgressivePreview from './NleProgressivePreview';
import { peekWarmedHls } from '../utils/nleWarmup';
import './NewNleEditor.css';

const SCRUB_SETTLE_MS = 120;
const PLAYHEAD_UI_MS = 50;
const PLAYLIST_REBUILD_MS = 500;
const TIMELINE_OVERSCAN_PX = 280;

const TRACK_LABELS = {
  video: 'Cuts',
  voiceover: 'Voiceover',
  music: 'Music',
  sfx: 'SFX',
  subtitles: 'Subtitles',
};

const NLE_RULER_H = 22;
const NLE_LANE_VIDEO = 88;
const NLE_LANE_AUDIO = 40;
const NLE_LANE_SFX = 44;
const NLE_LANE_SUB = 40;
const WAVE_BAR_WIDTH = 3;
const WAVE_BAR_GAP = 2;
const CUT_GAP_PX = 1;
const LONG_PRESS_MS = 160;

function TrackLabel({ id, active, onClick, height, children }) {
  return (
    <button
      type="button"
      className={`new-nle__track-label${active ? ' new-nle__track-label--active' : ''}`}
      style={height ? { height, flex: 'none', minHeight: height } : undefined}
      onClick={onClick}
    >
      {children || TRACK_LABELS[id] || id}
    </button>
  );
}

const POSTER_FRAME_CACHE = new Map();
const POSTER_FRAME_MAX = 96;
let posterFrameActive = 0;
const posterFrameQueue = [];

function rememberPosterFrame(key, value) {
  if (POSTER_FRAME_CACHE.has(key)) POSTER_FRAME_CACHE.delete(key);
  POSTER_FRAME_CACHE.set(key, value);
  while (POSTER_FRAME_CACHE.size > POSTER_FRAME_MAX) {
    const oldest = POSTER_FRAME_CACHE.keys().next().value;
    POSTER_FRAME_CACHE.delete(oldest);
  }
}

function enqueuePosterFrame(run) {
  return new Promise((resolve, reject) => {
    const job = async () => {
      posterFrameActive += 1;
      try {
        resolve(await run());
      } catch (err) {
        reject(err);
      } finally {
        posterFrameActive -= 1;
        const next = posterFrameQueue.shift();
        if (next) next();
      }
    };
    if (posterFrameActive < 3) job();
    else posterFrameQueue.push(job);
  });
}

/** Grab one frame from a proxy/HD URL when the cut has no JPEG poster. */
function captureCutFrame(url) {
  if (!url) return Promise.reject(new Error('no url'));
  if (POSTER_FRAME_CACHE.has(url)) return Promise.resolve(POSTER_FRAME_CACHE.get(url));
  return enqueuePosterFrame(() => new Promise((resolve, reject) => {
    if (POSTER_FRAME_CACHE.has(url)) {
      resolve(POSTER_FRAME_CACHE.get(url));
      return;
    }
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.crossOrigin = 'anonymous';
    let settled = false;
    const timer = setTimeout(() => finish(null), 4000);
    const finish = (dataUrl) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try { video.pause(); video.removeAttribute('src'); video.load(); } catch (_) {}
      video.remove();
      if (dataUrl) {
        rememberPosterFrame(url, dataUrl);
        resolve(dataUrl);
      } else reject(new Error('frame capture failed'));
    };
    video.onloadeddata = () => {
      try {
        if (video.videoWidth < 2) {
          finish(null);
          return;
        }
        try { video.currentTime = Math.min(0.08, (video.duration || 1) * 0.05); } catch (_) {}
      } catch (_) {
        finish(null);
      }
    };
    video.onseeked = () => {
      try {
        const w = Math.min(180, video.videoWidth || 180);
        const h = Math.round(w * ((video.videoHeight || 320) / (video.videoWidth || 180)));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          finish(null);
          return;
        }
        ctx.drawImage(video, 0, 0, w, h);
        finish(canvas.toDataURL('image/jpeg', 0.7));
      } catch (_) {
        finish(null);
      }
    };
    video.onerror = () => finish(null);
    video.src = url;
  }));
}

/**
 * True once the sentinel's parent tile has been near the viewport (sticky). Used to defer
 * expensive frame capture / <video> fallbacks for tiles far off screen (e.g. big cut picker).
 * Falls back to always-true when IntersectionObserver is unavailable or `disabled`.
 */
function useNearViewport(disabled, rootMargin = '240px') {
  const [near, setNear] = useState(() => disabled || typeof IntersectionObserver === 'undefined');
  // Callback ref (as state) so the observer attaches whenever the sentinel actually mounts.
  const [sentinel, setSentinel] = useState(null);
  useEffect(() => {
    if (near || !sentinel) return undefined;
    const target = sentinel.parentElement;
    if (!target) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setNear(true);
        io.disconnect();
      }
    }, { rootMargin });
    io.observe(target);
    return () => io.disconnect();
  }, [near, sentinel, rootMargin]);
  return [near, setSentinel];
}

const CutPoster = React.memo(function CutPoster({ cut, eager = false }) {
  const poster = cutPosterUrl(cut);
  const preview = cutPreviewUrl(cut);
  const [near, sentinelRef] = useNearViewport(eager);
  const [src, setSrc] = useState(() => {
    if (poster) return poster;
    if (preview && POSTER_FRAME_CACHE.has(preview)) return POSTER_FRAME_CACHE.get(preview);
    return '';
  });
  const [failedPoster, setFailedPoster] = useState(false);
  const [useVideoFallback, setUseVideoFallback] = useState(false);

  useEffect(() => {
    setUseVideoFallback(false);
    if (poster && !failedPoster) {
      setSrc(poster);
      return undefined;
    }
    if (!preview) {
      setSrc('');
      return undefined;
    }
    if (POSTER_FRAME_CACHE.has(preview)) {
      setSrc(POSTER_FRAME_CACHE.get(preview));
      return undefined;
    }
    // Don't spin up a hidden <video> decode for tiles that are nowhere near the viewport.
    if (!near) return undefined;
    let cancelled = false;
    captureCutFrame(preview)
      .then((dataUrl) => { if (!cancelled) setSrc(dataUrl); })
      .catch(() => {
        if (!cancelled) {
          setSrc('');
          // S3 often blocks canvas capture (tainted) — show a muted video frame instead.
          setUseVideoFallback(true);
        }
      });
    return () => { cancelled = true; };
  }, [poster, preview, failedPoster, near]);

  // Invisible marker so the observer can watch the parent tile until it's near the viewport.
  const sentinel = near ? null : (
    <span ref={sentinelRef} aria-hidden="true" style={{ display: 'none' }} />
  );

  if (useVideoFallback && preview) {
    if (!near) return sentinel;
    return (
      <video
        src={`${preview}#t=0.1`}
        muted
        playsInline
        preload={eager ? 'auto' : 'metadata'}
        draggable={false}
      />
    );
  }
  if (!src) return sentinel;
  return (
    <img
      src={src}
      alt=""
      draggable={false}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      onError={() => {
        if (poster && src === poster && !failedPoster) {
          setFailedPoster(true);
          setSrc('');
          return;
        }
        if (preview) setUseVideoFallback(true);
        setSrc('');
      }}
    />
  );
});

/** Bar heights only depend on (count, height); cache so re-mounts don't redo the trig. */
const WAVE_HEIGHTS_CACHE = new Map();
function waveBarHeights(count, height) {
  const key = `${count}:${height}`;
  let heights = WAVE_HEIGHTS_CACHE.get(key);
  if (!heights) {
    heights = new Array(count);
    for (let i = 0; i < count; i++) {
      const t = Math.abs(Math.sin(i * 0.83 + Math.sin(i * 0.31) * 2.7));
      heights[i] = Math.max(2, Math.round((0.22 + 0.68 * t) * (height - 4)));
    }
    if (WAVE_HEIGHTS_CACHE.size >= 8) WAVE_HEIGHTS_CACHE.delete(WAVE_HEIGHTS_CACHE.keys().next().value);
    WAVE_HEIGHTS_CACHE.set(key, heights);
  }
  return heights;
}

const FakeWaveform = React.memo(function FakeWaveform({ width, height = 28 }) {
  const bars = useMemo(() => {
    const count = Math.max(1, Math.floor(width / (WAVE_BAR_WIDTH + WAVE_BAR_GAP)));
    return waveBarHeights(count, height).map((h, i) => (
      <div
        key={i}
        className="new-nle__wave-bar"
        style={{ width: WAVE_BAR_WIDTH, height: h }}
      />
    ));
  }, [width, height]);
  return <div className="new-nle__waveform">{bars}</div>;
});

/** Ruler ticks only change with the timeline length, not with the playhead. */
const TimeRuler = React.memo(function TimeRuler({ totalSec, trackStart, fromSec = 0, toSec = Infinity }) {
  const secs = Math.ceil(totalSec) + 1;
  const items = useMemo(() => {
    const first = Math.max(0, Math.floor(fromSec));
    const last = Math.min(secs - 1, Math.ceil(toSec));
    const out = [];
    for (let sec = first; sec <= last; sec++) {
      out.push(
        <div
          key={`ruler-${sec}`}
          className="new-nle__ruler-sec"
          style={{ left: trackStart + sec * NEW_NLE_PX_PER_SEC, width: NEW_NLE_PX_PER_SEC }}
        >
          <span className="new-nle__ruler-label">{sec}s</span>
          {[1, 2, 3, 4].map((tick) => (
            <div
              key={tick}
              className="new-nle__ruler-tick"
              style={{ left: `${(tick / 5) * 100}%` }}
            />
          ))}
        </div>
      );
    }
    return out;
  }, [secs, trackStart, fromSec, toSec]);
  return (
    <div className="new-nle__ruler" style={{ height: NLE_RULER_H }}>
      {items}
    </div>
  );
});

/** Subtitle blocks depend on the script/timestamps, not on the playhead. */
const SubtitleLane = React.memo(function SubtitleLane({ blocks, trackStart, fromSec = 0, toSec = Infinity }) {
  const items = useMemo(() => blocks
    .filter((block) => block.end >= fromSec && block.start <= toSec)
    .map((block) => (
      <div
        key={block.id}
        className="new-nle__clip new-nle__clip--sub"
        style={{
          left: trackStart + block.start * NEW_NLE_PX_PER_SEC,
          width: Math.max(12, (block.end - block.start) * NEW_NLE_PX_PER_SEC),
        }}
        title={block.text}
      >
        {block.text}
      </div>
    )), [blocks, trackStart, fromSec, toSec]);
  return <>{items}</>;
});

/**
 * Cut picker grid. Memoized so it doesn't rebuild hundreds of tiles on every playhead tick.
 * `onAdd(cut)` / `onLoadMore()` must be stable (they forward to the latest editor closures).
 */
const CutsPickerGrid = React.memo(function CutsPickerGrid({
  availableCuts,
  cutUsageById,
  fastPacing,
  hasMoreCuts,
  loadingMoreCuts,
  onLoadMore,
  onAdd,
}) {
  return (
    <div
      className="new-nle__cuts-grid"
      onScroll={(e) => {
        if (!hasMoreCuts || loadingMoreCuts) return;
        const el = e.currentTarget;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) onLoadMore();
      }}
    >
      {(availableCuts || []).map((cut) => {
        const id = String(cut._id || cut.id || '');
        const usage = cutUsageById.get(id) || 0;
        const durLabel = cutPickerDurationLabel(cut, fastPacing);
        return (
          <div key={id} className="new-nle__cuts-thumb-wrap">
            <button
              type="button"
              className="new-nle__cuts-thumb"
              onClick={() => onAdd(cut)}
            >
              {(cutPosterUrl(cut) || cutPreviewUrl(cut)) ? (
                <CutPoster cut={cut} />
              ) : (
                <span>+</span>
              )}
              {usage > 0 && <span className="new-nle__cuts-usage">{usage}</span>}
            </button>
            <span className="new-nle__cuts-dur">{durLabel}</span>
          </div>
        );
      })}
      {loadingMoreCuts && <div className="new-nle__cuts-loading">Loading…</div>}
    </div>
  );
});

/**
 * One cut clip on the video lane. Memoized so playhead ticks don't re-render every clip.
 * `handlers` is a stable object that forwards to the latest editor closures.
 */
const CutClip = React.memo(function CutClip({
  cut,
  index,
  sec,
  width,
  translateX,
  isDragging,
  suppressTransition,
  eager,
  handlers,
}) {
  return (
    <div
      className={`new-nle__clip new-nle__clip--cut${isDragging ? ' new-nle__clip--dragging' : ' new-nle__clip--resting'}${suppressTransition ? ' new-nle__clip--no-transition' : ''}`}
      style={{
        left: 0,
        width,
        transform: `translateX(${translateX}px) scale(${isDragging ? 1.05 : 1})`,
      }}
      title={`Cut ${index + 1} · ${sec}s`}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => handlers.onPointerDown(index, e)}
      onPointerMove={(e) => handlers.onPointerMove(index, e)}
      onPointerUp={(e) => handlers.onPointerUp(index, e)}
      onPointerCancel={(e) => handlers.onPointerCancel(index, e)}
    >
      {(cutPosterUrl(cut) || cutPreviewUrl(cut)) ? (
        <CutPoster cut={cut} eager={eager} />
      ) : (
        <span>{index + 1}</span>
      )}
      <span className="new-nle__clip-dur">{sec}:00</span>
      <button
        type="button"
        className="new-nle__clip-remove"
        onClick={(ev) => {
          ev.stopPropagation();
          handlers.onRemove(index);
        }}
      >
        ×
      </button>
    </div>
  );
});

function offsetForItem(index, positions, slotPrefix) {
  if (!slotPrefix?.length) return 0;
  const usable = positions?.length && slotPrefix.length === positions.length + 1;
  const slot = usable ? positions[index] : index;
  return slotPrefix[slot] || 0;
}

function slotForCenterX(centerX, positions, cardWidths) {
  const n = cardWidths.length;
  if (positions.length !== n) return 0;
  const slotWidths = new Array(n);
  for (let i = 0; i < n; i++) slotWidths[positions[i]] = cardWidths[i];
  let acc = 0;
  for (let s = 0; s < n; s++) {
    const w = slotWidths[s] + CUT_GAP_PX;
    if (centerX < acc + w) return s;
    acc += w;
  }
  return n - 1;
}

function movePosition(positions, from, to) {
  const next = positions.slice();
  for (let i = 0; i < next.length; i++) {
    const p = next[i];
    if (p === from) next[i] = to;
    else if (from < to && p > from && p <= to) next[i] = p - 1;
    else if (to < from && p >= to && p < from) next[i] = p + 1;
  }
  return next;
}

function TitleCardLivePreview({
  styleId,
  words,
  playhead,
  primaryColor,
  accentColor,
  fontFamily,
  sizeClass,
}) {
  const list = words || [];
  if (styleId === 'style1') {
    const lines = [];
    for (let i = 0; i < list.length; i += 2) {
      lines.push(list.slice(i, i + 2));
    }
    const aligns = ['center', 'flex-start', 'flex-end', 'center'];
    return (
      <div className={`new-nle__title-overlay new-nle__title-overlay--stack ${sizeClass}`}>
        {lines.map((lineWords, li) => {
          const lineStart = lineWords[0]?.start ?? 0;
          const visible = playhead >= lineStart - 0.02;
          const color = li % 2 === 1 ? (accentColor || '#F5D66D') : (primaryColor || '#ffffff');
          const font = li % 2 === 1
            ? 'Arial Black, Arial, sans-serif'
            : (fontFamily || 'Georgia, serif');
          return (
            <div
              key={`line-${li}`}
              className={`new-nle__title-line${visible ? ' new-nle__title-line--in' : ''}`}
              style={{
                justifyContent: aligns[li % aligns.length],
                color,
                fontFamily: font,
              }}
            >
              {lineWords.map((w) => w.text).join(' ')}
            </div>
          );
        })}
      </div>
    );
  }
  if (styleId === 'style2') {
    const rows = [];
    for (let i = 0; i < list.length; i += 2) {
      rows.push(list.slice(i, i + 2));
    }
    return (
      <div className={`new-nle__title-overlay new-nle__title-overlay--stack new-nle__title-overlay--style2 ${sizeClass}`}>
        {rows.map((rowWords, ri) => (
          <div key={`row-${ri}`} className="new-nle__title-row">
            {rowWords.map((w, wi) => {
              const visible = playhead >= (w.start ?? 0) - 0.02;
              return (
                <span
                  key={`${ri}-${wi}-${w.text}`}
                  className={`new-nle__title-word${visible ? ' new-nle__title-word--in' : ''}`}
                  style={{ color: primaryColor || '#ffffff', fontFamily: fontFamily || undefined }}
                >
                  {w.text}
                </span>
              );
            })}
          </div>
        ))}
      </div>
    );
  }
  if (styleId === 'style3') {
    // Same pairing as Style 2, but full line at once; top line stays fixed (stack grows down).
    const LINES_PER_PAGE = 3;
    const lines = [];
    for (let i = 0; i < list.length; i += 2) {
      lines.push(list.slice(i, i + 2));
    }
    let pageIndex = 0;
    for (let li = 0; li < lines.length; li++) {
      const lineStart = lines[li][0]?.start ?? 0;
      const pageEndLi = Math.min(lines.length, (Math.floor(li / LINES_PER_PAGE) + 1) * LINES_PER_PAGE) - 1;
      const pageEnd = lines[pageEndLi][lines[pageEndLi].length - 1]?.end
        ?? lines[pageEndLi][lines[pageEndLi].length - 1]?.start
        ?? lineStart;
      if (playhead >= lineStart - 0.02 && playhead < pageEnd + 0.04) {
        pageIndex = Math.floor(li / LINES_PER_PAGE);
      }
    }
    const pageLines = lines.slice(pageIndex * LINES_PER_PAGE, pageIndex * LINES_PER_PAGE + LINES_PER_PAGE);
    return (
      <div className={`new-nle__title-overlay new-nle__title-overlay--stack new-nle__title-overlay--style3 ${sizeClass}`}>
        {pageLines.map((rowWords, ri) => {
          const lineStart = rowWords[0]?.start ?? 0;
          const visible = playhead >= lineStart - 0.02;
          return (
            <div
              key={`s3-${pageIndex}-${ri}`}
              className={`new-nle__title-line${visible ? ' new-nle__title-line--in' : ''}`}
              style={{
                justifyContent: 'center',
                color: primaryColor || '#ffffff',
                fontFamily: fontFamily || undefined,
              }}
            >
              {rowWords.map((w) => w.text).join(' ')}
            </div>
          );
        })}
      </div>
    );
  }
  return (
    <div
      className={`new-nle__title-overlay ${sizeClass}`}
      style={{ color: primaryColor || '#ffffff', fontFamily: fontFamily || undefined }}
    >
      {list.map((w) => w.text).join(' ')}
    </div>
  );
}

function StyleHoverDemo({ styleId, primary, accent }) {
  if (styleId === 'style1') {
    const lines = [
      { text: 'THIS IS', color: primary, font: 'Georgia, serif', align: 'center', delay: '0s' },
      { text: 'YOUR', color: accent, font: 'Arial Black, Arial, sans-serif', align: 'flex-start', delay: '0.35s' },
      { text: 'TITLE', color: accent, font: 'Georgia, serif', align: 'flex-end', delay: '0.7s' },
      { text: 'CARD', color: primary, font: 'Arial Black, Arial, sans-serif', align: 'center', delay: '1.05s' },
    ];
    return (
      <div className="new-nle__style-demo">
        {lines.map((line) => (
          <div
            key={line.text}
            className="new-nle__style-demo-line"
            style={{
              justifyContent: line.align === 'center' ? 'center' : (line.align === 'flex-start' ? 'flex-start' : 'flex-end'),
              color: line.color,
              fontFamily: line.font,
              animationDelay: line.delay,
            }}
          >
            {line.text}
          </div>
        ))}
      </div>
    );
  }
  if (styleId === 'style3') {
    const lines = ['THIS IS', 'YOUR TITLE', 'CARD'];
    return (
      <div className="new-nle__style-demo new-nle__style-demo--style3">
        {lines.map((line, li) => (
          <div
            key={line}
            className="new-nle__style-demo-line"
            style={{
              justifyContent: 'center',
              color: primary,
              fontFamily: 'Arial Black, Arial, sans-serif',
              animationDelay: `${li * 0.7}s`,
            }}
          >
            {line}
          </div>
        ))}
      </div>
    );
  }
  const words = ['THIS', 'IS', 'YOUR', 'TITLE', 'CARD'];
  return (
    <div className="new-nle__style-demo new-nle__style-demo--style2">
      {[0, 1, 2].map((row) => {
        const rowWords = words.slice(row * 2, row * 2 + 2);
        if (!rowWords.length) return null;
        return (
          <div key={`r-${row}`} className="new-nle__style-demo-row">
            {rowWords.map((word, wi) => (
              <span
                key={`${row}-${word}`}
                className="new-nle__style-demo-word"
                style={{ color: primary, animationDelay: `${(row * 2 + wi) * 0.35}s` }}
              >
                {word}
              </span>
            ))}
          </div>
        );
      })}
    </div>
  );
}

export default function NewNleEditor({
  open,
  onClose,
  isBoukar = false,
  voiceoverUrl,
  voiceoverTimestamps,
  scriptContent,
  initialCuts = [],
  availableCuts = [],
  availableMusic = [],
  selectedMusicId: initialMusicId = null,
  useMusic: initialUseMusic = true,
  flipbookIntro: initialFlipbook = false,
  cutSilenceMode: initialCutSilence = false,
  fastPacing: initialFastPacing = false,
  enableSubtitles: initialEnableSubtitles = true,
  oneWordPerSubtitle: initialOneWord = false,
  subtitlePosition: initialSubtitlePosition = 'bottom',
  bigText: initialBigText = false,
  availableFonts = [],
  selectedFontId: initialFontId = '',
  subtitleFontColor: initialFontColor = '#ffffff',
  subtitleOutlineColor: initialOutlineColor = '#000000',
  subtitleOutlineEnabled: initialOutlineEnabled = false,
  openingLineInCenter: initialTitleCard = false,
  openingLineTextSize: initialTitleSize = 'big',
  openingLineFontId: initialTitleFontId = '',
  openingLineFontColor: initialTitleFontColor = '#ffffff',
  fontMixing: initialFontMixing = false,
  centerStackOpening: initialCenterStack = false,
  centerStackLineByLine: initialCenterStackLineByLine = false,
  fontMixingSecondColor: initialSecondColor = '#F5D66D',
  includeTags: initialIncludeTags = [],
  excludeTags: initialExcludeTags = [],
  availableTags: availableTagsProp = [],
  onTagsChange,
  loading = false,
  planningSfx = false,
  exporting = false,
  onRequestAutoSfx,
  onLoadMoreCuts,
  hasMoreCuts = false,
  loadingMoreCuts = false,
  onExport,
  workspaceId = null,
  darkMode = true,
}) {
  const [cuts, setCuts] = useState([]);
  const [musicId, setMusicId] = useState(initialMusicId || '');
  const [useMusic, setUseMusic] = useState(initialUseMusic);
  const [flipbookIntro, setFlipbookIntro] = useState(initialFlipbook);
  const [cutSilence, setCutSilence] = useState(initialCutSilence);
  const [fastPacing, setFastPacing] = useState(!!initialFastPacing);
  const [enableSubtitles, setEnableSubtitles] = useState(initialEnableSubtitles);
  const [oneWordPerSubtitle, setOneWordPerSubtitle] = useState(initialOneWord);
  const [subtitlePosition, setSubtitlePosition] = useState(initialSubtitlePosition === 'center' ? 'center' : 'bottom');
  const [bigText, setBigText] = useState(!!initialBigText);
  const [includeTagInput, setIncludeTagInput] = useState('');
  const [excludeTagInput, setExcludeTagInput] = useState('');
  const [localIncludeTags, setLocalIncludeTags] = useState(initialIncludeTags || []);
  const [localExcludeTags, setLocalExcludeTags] = useState(initialExcludeTags || []);
  const [showCutsFilter, setShowCutsFilter] = useState(false);
  const [showCutsPicker, setShowCutsPicker] = useState(false);
  const [showIncludeSuggestions, setShowIncludeSuggestions] = useState(false);
  const [showExcludeSuggestions, setShowExcludeSuggestions] = useState(false);
  const [isNarrow, setIsNarrow] = useState(false);
  const [positions, setPositions] = useState([]);
  const [draggingIndex, setDraggingIndex] = useState(-1);
  const [dragX, setDragX] = useState(0);
  const [suppressTransition, setSuppressTransition] = useState(false);
  const [previewMusicId, setPreviewMusicId] = useState(null);
  const [styleHover, setStyleHover] = useState(null);
  const [fontId, setFontId] = useState(initialFontId || '');
  const [fontColor, setFontColor] = useState(initialFontColor || '#ffffff');
  const [outlineColor, setOutlineColor] = useState(initialOutlineColor || '#000000');
  const [outlineEnabled, setOutlineEnabled] = useState(!!initialOutlineEnabled);
  const [titleCard, setTitleCard] = useState(!!initialTitleCard);
  const [titleTextSize, setTitleTextSize] = useState(initialTitleSize === 'small' ? 'small' : 'big');
  const [titleFontId, setTitleFontId] = useState(initialTitleFontId || '');
  const [titleFontColor, setTitleFontColor] = useState(initialTitleFontColor || '#ffffff');
  const [fontMixing, setFontMixing] = useState(!!initialFontMixing);
  const [centerStackOpening, setCenterStackOpening] = useState(!!initialCenterStack);
  const [centerStackLineByLine, setCenterStackLineByLine] = useState(!!initialCenterStackLineByLine);
  const [secondColor, setSecondColor] = useState(initialSecondColor || '#F5D66D');
  const [sfxCues, setSfxCues] = useState([]);
  const [playhead, setPlayhead] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [activeTrack, setActiveTrack] = useState('video');
  const [showMusicModal, setShowMusicModal] = useState(false);
  const [showSfxModal, setShowSfxModal] = useState(false);
  const [sfxModalTime, setSfxModalTime] = useState(0);
  const [previewSfxId, setPreviewSfxId] = useState(null);
  const [draggingSfxId, setDraggingSfxId] = useState(null);
  const [viewportW, setViewportW] = useState(640);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [hlsUri, setHlsUri] = useState('');
  const [previewMode, setPreviewMode] = useState('none');
  const [previewRebuilding, setPreviewRebuilding] = useState(false);

  const voRef = useRef(null);
  const musicRef = useRef(null);
  const sfxPreviewRef = useRef(null);
  const tracksScrollRef = useRef(null);
  const tracksViewportRef = useRef(null);
  const playheadRef = useRef(0);
  const rafRef = useRef(null);
  const wasPlayingRef = useRef(false);
  const programmaticScrollRef = useRef(false);
  const programmaticClearRafRef = useRef(0);
  const userScrubbingRef = useRef(false);
  const scrubEndTimerRef = useRef(null);
  const pendingScrollTimeRef = useRef(null);
  const introSfxRef = useRef(null);
  const introSfxPlayedRef = useRef(false);
  const musicPreviewRef = useRef(null);
  const positionsRef = useRef([]);
  const suppressTransitionTimerRef = useRef(null);
  const sfxLastTRef = useRef(0);
  const sfxPlayingRef = useRef([]);
  const sfxProtoRef = useRef(new Map());
  const isScrubbingRef = useRef(false);
  const lastUiPlayheadAtRef = useRef(0);
  const playlistRequestRef = useRef(0);
  const loadedPlaylistKeyRef = useRef('');
  const hlsUriRef = useRef('');
  const hlsBlobRef = useRef('');
  const playingRef = useRef(false);

  const revokeHlsBlob = () => {
    if (!hlsBlobRef.current) return;
    try { URL.revokeObjectURL(hlsBlobRef.current); } catch (_) {}
    hlsBlobRef.current = '';
  };
  const dragStartXRef = useRef(0);
  const dragStartScrollRef = useRef(0);
  const dragStartClientXRef = useRef(0);
  const pressTimerRef = useRef(null);
  playingRef.current = !!playing;

  const allowAutoSeedRef = useRef(true);
  /** True while parent is still resolving script.usedCutIds — blocks pool auto-seed. */
  const awaitingServerCutsRef = useRef(false);
  const sfxArmedRef = useRef(new Set());
  const [measuredVoSec, setMeasuredVoSec] = useState(0);

  const voDuration = useMemo(() => {
    const ends = voiceoverTimestamps?.character_end_times_seconds;
    if (Array.isArray(ends) && ends.length) {
      const last = ends[ends.length - 1];
      if (Number.isFinite(last) && last > 0) return last;
    }
    if (Number.isFinite(measuredVoSec) && measuredVoSec > 0) return measuredVoSec;
    return 0;
  }, [voiceoverTimestamps, measuredVoSec]);

  const requiredSec = useMemo(
    () => resolveRequiredSeconds({ voiceoverSeconds: voDuration, flipbookIntro }),
    [voDuration, flipbookIntro]
  );

  const totalTimelineSec = useMemo(() => {
    const cutsSec = sumCutsSeconds(cuts, fastPacing);
    const intro = flipbookIntro ? FLIPBOOK_INTRO_DURATION_SEC : 0;
    // VO runs from t=0 with the intro; visual length is intro + main cuts ≈ VO duration.
    return Math.max(voDuration, cutsSec + intro, 1);
  }, [cuts, fastPacing, flipbookIntro, voDuration]);

  const trackWidth = Math.max(320, totalTimelineSec * NEW_NLE_PX_PER_SEC);
  const trackStart = viewportW / 2;
  const contentWidth = trackWidth + viewportW;

  const subtitles = useMemo(
    () => (enableSubtitles ? buildSubtitleBlocks(scriptContent, voiceoverTimestamps, oneWordPerSubtitle, voDuration) : []),
    [enableSubtitles, scriptContent, voiceoverTimestamps, oneWordPerSubtitle, voDuration]
  );

  const selectedMusic = useMemo(
    () => availableMusic.find((m) => String(m._id) === String(musicId)) || null,
    [availableMusic, musicId]
  );

  const studioFonts = useMemo(() => filterStudioFonts(availableFonts), [availableFonts]);

  const selectedFont = useMemo(
    () => (studioFonts || []).find((f) => String(f._id) === String(fontId)) || null,
    [studioFonts, fontId]
  );

  const selectedTitleFont = useMemo(
    () => (studioFonts || []).find((f) => String(f._id) === String(titleFontId)) || null,
    [studioFonts, titleFontId]
  );

  const titleCardStyle = fontMixing
    ? 'style1'
    : (centerStackOpening ? 'style2' : (centerStackLineByLine ? 'style3' : 'standard'));
  const setTitleCardStyle = (style) => {
    setFontMixing(style === 'style1');
    setCenterStackOpening(style === 'style2');
    setCenterStackLineByLine(style === 'style3');
  };

  const titleCardText = useMemo(() => {
    const first = extractFirstSentence(scriptContent);
    return first || 'Your title card';
  }, [scriptContent]);

  const titleCardEndSec = useMemo(
    () => firstSentenceEndSeconds(scriptContent, voiceoverTimestamps),
    [scriptContent, voiceoverTimestamps]
  );

  const titleTimedWords = useMemo(
    () => firstSentenceTimedWords(scriptContent, voiceoverTimestamps),
    [scriptContent, voiceoverTimestamps]
  );

  const previewSubtitleStyle = useMemo(() => {
    const style = {
      color: fontColor || '#ffffff',
      fontFamily: selectedFont?.name ? `"${selectedFont.name}", sans-serif` : undefined,
      textShadow: 'none',
    };
    if (outlineEnabled) {
      const o = outlineColor || '#000000';
      style.WebkitTextStroke = `1.5px ${o}`;
      style.paintOrder = 'stroke fill';
      style.textShadow = `0 0 0 ${o}, 1px 0 0 ${o}, -1px 0 0 ${o}, 0 1px 0 ${o}, 0 -1px 0 ${o}`;
    }
    return style;
  }, [fontColor, selectedFont, outlineEnabled, outlineColor]);

  const cutUsageById = useMemo(() => cutUsageCounts(cuts), [cuts]);

  const availableTags = useMemo(() => {
    const excluded = new Set(['auto-generated', 'script-generated', 'voiceover']);
    const fromProp = (availableTagsProp || []).map((t) => String(t).trim()).filter(Boolean);
    if (fromProp.length) {
      return [...new Set(fromProp.filter((t) => !excluded.has(t.toLowerCase())))].sort((a, b) => a.localeCompare(b));
    }
    const set = new Set();
    (availableCuts || []).forEach((c) => {
      (c.tags || []).forEach((t) => {
        const tag = String(t || '').trim();
        if (tag && !excluded.has(tag.toLowerCase())) set.add(tag);
      });
    });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [availableTagsProp, availableCuts]);

  const filterTagSuggestions = useCallback((inputValue, alreadySelected) => {
    const selected = new Set((alreadySelected || []).map((t) => String(t).toLowerCase()));
    const q = String(inputValue || '').trim().toLowerCase();
    return availableTags
      .filter((t) => !selected.has(t.toLowerCase()))
      .filter((t) => !q || t.toLowerCase().includes(q))
      .slice(0, 16);
  }, [availableTags]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width: 720px)');
    const apply = () => setIsNarrow(!!mq.matches);
    apply();
    if (mq.addEventListener) mq.addEventListener('change', apply);
    else mq.addListener(apply);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', apply);
      else mq.removeListener(apply);
    };
  }, []);

  const cardWidths = useMemo(
    () => cuts.map((c) => Math.max(8, cutEffectiveSeconds(c, fastPacing) * NEW_NLE_PX_PER_SEC - CUT_GAP_PX)),
    [cuts, fastPacing]
  );

  const slotPrefix = useMemo(() => {
    const n = cardWidths.length;
    if (!n) return [0];
    const slotWidths = new Array(n);
    const pos = positions.length === n ? positions : cuts.map((_, i) => i);
    for (let i = 0; i < n; i++) slotWidths[pos[i]] = cardWidths[i];
    return buildCardPrefixOffsets(slotWidths, CUT_GAP_PX);
  }, [cardWidths, positions, cuts]);

  const visibleCutIndexes = useMemo(() => {
    const n = cuts.length;
    if (!n) return [];
    const viewLeft = Math.max(0, timelinePxFromTime(playhead) - TIMELINE_OVERSCAN_PX);
    const viewRight = timelinePxFromTime(playhead) + viewportW + TIMELINE_OVERSCAN_PX;
    const out = [];
    for (let i = 0; i < n; i++) {
      const x = trackStart + (flipbookIntro ? FLIPBOOK_INTRO_DURATION_SEC * NEW_NLE_PX_PER_SEC : 0) + offsetForItem(i, positions, slotPrefix);
      const w = cardWidths[i] || 8;
      if (x + w >= viewLeft && x <= viewRight) out.push(i);
      if (draggingIndex === i && !out.includes(i)) out.push(i);
    }
    return out.length ? out : cuts.map((_, i) => i);
  }, [cuts, playhead, viewportW, trackStart, flipbookIntro, positions, slotPrefix, cardWidths, draggingIndex]);

  const snapPositionsWithoutTransition = useCallback((nextPositions) => {
    setSuppressTransition(true);
    setPositions((prev) => {
      if (
        prev.length === nextPositions.length
        && prev.every((p, i) => p === nextPositions[i])
      ) {
        return prev;
      }
      return nextPositions;
    });
    if (suppressTransitionTimerRef.current) clearTimeout(suppressTransitionTimerRef.current);
    suppressTransitionTimerRef.current = setTimeout(() => {
      suppressTransitionTimerRef.current = null;
      setSuppressTransition(false);
    }, 50);
  }, []);

  useEffect(() => {
    positionsRef.current = positions;
  }, [positions]);

  useEffect(() => {
    snapPositionsWithoutTransition(cuts.map((_, i) => i));
  }, [cuts, fastPacing, snapPositionsWithoutTransition]);

  const flashPool = useMemo(() => {
    const fromCuts = cuts?.length ? cuts : [];
    const fromAvail = availableCuts?.length ? availableCuts : [];
    return fromCuts.length >= 3 ? fromCuts : (fromAvail.length ? fromAvail : fromCuts);
  }, [cuts, availableCuts]);

  const activePreview = useMemo(
    () => activeCutIndexAtTime(cuts, playhead, fastPacing, flipbookIntro, flashPool),
    [cuts, playhead, fastPacing, flipbookIntro, flashPool]
  );

  // Seed cuts when opening
  useEffect(() => {
    if (!open) return;
    setMusicId(initialMusicId || '');
    setUseMusic(initialUseMusic);
    setFlipbookIntro(initialFlipbook);
    setCutSilence(initialCutSilence);
    setFastPacing(!!initialFastPacing);
    setEnableSubtitles(initialEnableSubtitles);
    setOneWordPerSubtitle(initialOneWord);
    setSubtitlePosition(initialSubtitlePosition === 'center' ? 'center' : 'bottom');
    setBigText(!!initialBigText);
    setLocalIncludeTags(initialIncludeTags || []);
    setLocalExcludeTags(initialExcludeTags || []);
    setIncludeTagInput('');
    setExcludeTagInput('');
    setFontId(initialFontId || '');
    setFontColor(initialFontColor || '#ffffff');
    setOutlineColor(initialOutlineColor || '#000000');
    setOutlineEnabled(!!initialOutlineEnabled);
    setTitleCard(!!initialTitleCard);
    setTitleTextSize(initialTitleSize === 'small' ? 'small' : 'big');
    setTitleFontId(initialTitleFontId || '');
    setTitleFontColor(initialTitleFontColor || '#ffffff');
    setFontMixing(!!initialFontMixing);
    setCenterStackOpening(!!initialCenterStack);
    setCenterStackLineByLine(!!initialCenterStackLineByLine);
    setSecondColor(initialSecondColor || '#F5D66D');
    setSfxCues([]);
    setPlayhead(0);
    playheadRef.current = 0;
    playingRef.current = false;
    setPlaying(false);
    setActiveTrack('video');
    setShowCutsPicker(false);
    setShowCutsFilter(false);
    setDraggingIndex(-1);
    setStyleHover(null);
    setPreviewMusicId(null);
    musicPreviewRef.current?.pause();
    setMeasuredVoSec(0);
    allowAutoSeedRef.current = true;
    awaitingServerCutsRef.current = !!loading;
    sfxArmedRef.current = new Set();

    // Prefer ordered initial cuts from Lambda/script. While loading usedCutIds,
    // do NOT seed from the available pool (that shows "latest cuts").
    if (loading) {
      setCuts([]);
    } else if (initialCuts?.length) {
      setCuts(stampCutKeys(initialCuts));
      allowAutoSeedRef.current = false;
      awaitingServerCutsRef.current = false;
    } else if (availableCuts?.length && requiredSec > 0) {
      setCuts(stampCutKeys(seedCutsToDuration(availableCuts, requiredSec, !!initialFastPacing)));
      allowAutoSeedRef.current = false;
    } else {
      setCuts([]);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) preloadHlsEngine();
  }, [open]);

  // Keep centered playhead aligned after open / viewport resize
  useEffect(() => {
    if (!open) return;
    const el = tracksScrollRef.current;
    if (!el) return;
    programmaticScrollRef.current = true;
    el.scrollLeft = timelinePxFromTime(playheadRef.current);
    requestAnimationFrame(() => {
      programmaticScrollRef.current = false;
    });
  }, [open, viewportW]);

  // Apply server timeline when usedCutIds finish loading; only then fall back to pool seed.
  useEffect(() => {
    if (!open) return;
    if (loading) {
      awaitingServerCutsRef.current = true;
      return;
    }
    if (awaitingServerCutsRef.current || allowAutoSeedRef.current) {
      if (initialCuts?.length) {
        setCuts(stampCutKeys(initialCuts));
        awaitingServerCutsRef.current = false;
        allowAutoSeedRef.current = false;
        return;
      }
      awaitingServerCutsRef.current = false;
      if (!allowAutoSeedRef.current || cuts.length) return;
      if (!availableCuts?.length) return;
      const need = resolveRequiredSeconds({ voiceoverSeconds: voDuration, flipbookIntro });
      if (need > 0) {
        setCuts(stampCutKeys(seedCutsToDuration(availableCuts, need, fastPacing)));
        allowAutoSeedRef.current = false;
      }
    }
  }, [open, loading, initialCuts, availableCuts, cuts.length, voDuration, flipbookIntro, fastPacing]);

  const stopAllSfx = useCallback(() => {
    sfxPlayingRef.current.forEach((a) => {
      try { a.pause(); } catch (_) {}
    });
    sfxPlayingRef.current = [];
  }, []);

  const playTimelineSfx = useCallback((url, offsetSec = 0) => {
    if (!url) return;
    try {
      const proto = sfxProtoRef.current.get(url);
      const a = proto ? proto.cloneNode(true) : new Audio(url);
      a.volume = 0.85;
      const offset = Math.max(0, Number(offsetSec) || 0);
      if (offset > 0.02) {
        const startAt = () => {
          try { a.currentTime = offset; } catch (_) {}
          a.play().catch(() => {});
        };
        if (a.readyState >= 1) startAt();
        else a.addEventListener('loadedmetadata', startAt, { once: true });
      } else {
        a.play().catch(() => {});
      }
      sfxPlayingRef.current.push(a);
      a.onended = () => {
        sfxPlayingRef.current = sfxPlayingRef.current.filter((x) => x !== a);
      };
    } catch (_) {}
  }, []);

  const fireSfxCuesCrossing = useCallback((t) => {
    if (isScrubbingRef.current) return;
    const prev = sfxLastTRef.current;
    if (t + 0.08 < prev) {
      sfxLastTRef.current = t;
      sfxArmedRef.current = new Set();
      return;
    }
    sfxCues.forEach((cue) => {
      const start = Number(cue.startTime);
      if (!Number.isFinite(start) || !cue.url) return;
      const id = cue.clientId || `${cue.id}:${start}`;
      // Crossing detector: include t=0 by treating prev as exclusive lower bound.
      if (prev < start && t >= start) {
        if (!sfxArmedRef.current.has(id)) {
          sfxArmedRef.current.add(id);
          playTimelineSfx(cue.url, 0);
        }
      }
    });
    sfxLastTRef.current = t;
  }, [sfxCues, playTimelineSfx]);

  const armSfxAtPlayhead = useCallback((t) => {
    sfxArmedRef.current = new Set();
    const time = Math.max(0, Number(t) || 0);
    sfxCues.forEach((cue) => {
      const start = Number(cue.startTime);
      if (!Number.isFinite(start) || !cue.url) return;
      const dur = sfxCueDuration(cue);
      const id = cue.clientId || `${cue.id}:${start}`;
      if (start <= time && time < start + dur) {
        sfxArmedRef.current.add(id);
        playTimelineSfx(cue.url, time - start);
      } else if (start < time) {
        sfxArmedRef.current.add(id);
      }
    });
    sfxLastTRef.current = time;
  }, [sfxCues, playTimelineSfx]);

  useEffect(() => {
    if (!open) return undefined;
    const urls = new Set();
    sfxCues.forEach((c) => { if (c.url) urls.add(c.url); });
    // Only warm the full catalog once the user actually opens the SFX picker;
    // until then just the cues already on the timeline are preloaded.
    if (showSfxModal) SFX_CATALOG.forEach((s) => urls.add(buildSfxUrl(s.file)));
    urls.forEach((url) => {
      if (sfxProtoRef.current.has(url)) return;
      const a = new Audio();
      a.preload = 'auto';
      a.src = url;
      try { a.load(); } catch (_) {}
      sfxProtoRef.current.set(url, a);
    });
    return undefined;
  }, [open, sfxCues, showSfxModal]);

  const syncPlayhead = useCallback((t, { forceUi = false } = {}) => {
    const clamped = Math.max(0, Math.min(t, totalTimelineSec));
    playheadRef.current = clamped;
    const now = Date.now();
    if (forceUi || now - lastUiPlayheadAtRef.current >= PLAYHEAD_UI_MS) {
      lastUiPlayheadAtRef.current = now;
      setPlayhead(clamped);
    }
  }, [totalTimelineSec]);

  const scrollToTime = useCallback((t, { seekMedia = true } = {}) => {
    const clamped = Math.max(0, Math.min(t, totalTimelineSec));
    if (clamped <= 0.12) introSfxPlayedRef.current = false;
    if (clamped + 0.08 < playheadRef.current) sfxLastTRef.current = clamped;
    playheadRef.current = clamped;
    lastUiPlayheadAtRef.current = Date.now();
    setPlayhead(clamped);
    const el = tracksScrollRef.current;
    if (el) {
      programmaticScrollRef.current = true;
      el.scrollLeft = timelinePxFromTime(clamped);
      requestAnimationFrame(() => {
        programmaticScrollRef.current = false;
      });
    }
    if (seekMedia && voRef.current) {
      try {
        voRef.current.currentTime = Math.min(clamped, voRef.current.duration || clamped);
      } catch (_) {}
    }
  }, [totalTimelineSec]);

  const beginUserScrub = useCallback(() => {
    if (isScrubbingRef.current) return;
    isScrubbingRef.current = true;
    userScrubbingRef.current = true;
    setIsScrubbing(true);
    stopAllSfx();
    if (playingRef.current) {
      voRef.current?.pause();
      musicRef.current?.pause();
      introSfxRef.current?.pause();
      playingRef.current = false;
      setPlaying(false);
    }
  }, [stopAllSfx]);

  const finishUserScrub = useCallback((t) => {
    if (scrubEndTimerRef.current) {
      clearTimeout(scrubEndTimerRef.current);
      scrubEndTimerRef.current = null;
    }
    const clamped = Math.max(0, Math.min(Number.isFinite(t) ? t : playheadRef.current, totalTimelineSec));
    playheadRef.current = clamped;
    lastUiPlayheadAtRef.current = Date.now();
    setPlayhead(clamped);
    stopAllSfx();
    armSfxAtPlayhead(clamped);
    if (voRef.current) {
      try { voRef.current.currentTime = Math.min(clamped, voRef.current.duration || clamped); } catch (_) {}
    }
    if (musicRef.current && useMusic && selectedMusic?.url) {
      try { musicRef.current.currentTime = Math.min(clamped, musicRef.current.duration || clamped); } catch (_) {}
    }
    isScrubbingRef.current = false;
    userScrubbingRef.current = false;
    setIsScrubbing(false);
  }, [totalTimelineSec, useMusic, selectedMusic, stopAllSfx, armSfxAtPlayhead]);

  // Measure scroll viewport for centered playhead padding
  useEffect(() => {
    if (!open) return undefined;
    const el = tracksScrollRef.current || tracksViewportRef.current;
    if (!el) return undefined;
    const measure = () => setViewportW(el.clientWidth || 640);
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [open]);

  // After add-cut: scroll so playhead sits just past mid of the new cut
  useEffect(() => {
    if (pendingScrollTimeRef.current == null) return;
    const t = pendingScrollTimeRef.current;
    pendingScrollTimeRef.current = null;
    scrollToTime(t);
  }, [cuts, scrollToTime]);

  const playIntroSfxIfNeeded = useCallback((t) => {
    if (!flipbookIntro) return;
    if ((Number(t) || 0) > 0.12) return;
    if (introSfxPlayedRef.current) return;
    const a = introSfxRef.current;
    if (!a) return;
    introSfxPlayedRef.current = true;
    try {
      a.currentTime = 0;
      a.volume = 0.85;
      a.play().catch(() => {});
    } catch (_) {}
  }, [flipbookIntro]);

  const tick = useCallback(() => {
    const el = voRef.current;
    if (!el || isScrubbingRef.current) return;
    const t = el.currentTime || 0;
    syncPlayhead(t);
    playIntroSfxIfNeeded(t);
    fireSfxCuesCrossing(t);

    const scrollEl = tracksScrollRef.current;
    if (!userScrubbingRef.current && scrollEl) {
      const px = timelinePxFromTime(t);
      // Skip no-op writes (no scroll event fires, so nothing to guard) and share a single
      // pending rAF for the "programmatic" flag instead of allocating one per frame.
      if (Math.abs(scrollEl.scrollLeft - px) >= 0.5) {
        programmaticScrollRef.current = true;
        scrollEl.scrollLeft = px;
        if (!programmaticClearRafRef.current) {
          programmaticClearRafRef.current = requestAnimationFrame(() => {
            programmaticClearRafRef.current = 0;
            programmaticScrollRef.current = false;
          });
        }
      }
    }
    if (!el.paused) rafRef.current = requestAnimationFrame(tick);
  }, [syncPlayhead, playIntroSfxIfNeeded, fireSfxCuesCrossing]);

  useEffect(() => {
    if (!playing) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return undefined;
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [playing, tick]);

  // Composed HLS for Safari (native) and Chrome (hls.js).
  // Edits switch to progressive A/B immediately; HLS rebuilds in the background.
  useEffect(() => {
    if (!open || !workspaceId) {
      playlistRequestRef.current += 1;
      setPreviewRebuilding(false);
      return undefined;
    }
    if (cuts.length === 0) {
      playlistRequestRef.current += 1;
      loadedPlaylistKeyRef.current = '';
      hlsUriRef.current = '';
      revokeHlsBlob();
      setHlsUri('');
      setPreviewMode('none');
      setPreviewRebuilding(false);
      return undefined;
    }
    const cutIds = cuts.map((cut) => String(cut?._id || cut?.id || '')).filter(Boolean);
    if (!cutIds.length) {
      playlistRequestRef.current += 1;
      setPreviewMode('progressive');
      setPreviewRebuilding(false);
      return undefined;
    }
    const playlistKey = `${fastPacing ? 1 : 0}:${flipbookIntro ? 1 : 0}:${cutIds.join(',')}`;
    if (playlistKey === loadedPlaylistKeyRef.current && hlsUriRef.current) {
      setPreviewRebuilding(false);
      return undefined;
    }

    const applyHlsPayload = (data, requestId) => {
      if (requestId != null && requestId !== playlistRequestRef.current) return false;
      if (data?.mode === 'hls' && (data.playlistUrl || data.playlist)) {
        loadedPlaylistKeyRef.current = playlistKey;
        revokeHlsBlob();
        const native = typeof document !== 'undefined'
          && !!document.createElement('video').canPlayType('application/vnd.apple.mpegurl');
        let uri = data.playlistUrl || '';
        if (!native && data.playlist) {
          const blob = new Blob([data.playlist], { type: 'application/vnd.apple.mpegurl' });
          uri = URL.createObjectURL(blob);
          hlsBlobRef.current = uri;
        }
        if (!uri) {
          hlsUriRef.current = '';
          setHlsUri('');
          setPreviewMode('progressive');
          setPreviewRebuilding(false);
          return true;
        }
        hlsUriRef.current = uri;
        setHlsUri(uri);
        setPreviewMode('hls');
        setPreviewRebuilding(false);
        return true;
      }
      return false;
    };

    // Interactive path: do not wait on a remote playlist after an edit.
    setPreviewMode('progressive');
    setPreviewRebuilding(true);
    const requestId = ++playlistRequestRef.current;

    const warmed = peekWarmedHls(playlistKey);
    if (applyHlsPayload(warmed, requestId)) return undefined;

    const delayMs = loadedPlaylistKeyRef.current ? PLAYLIST_REBUILD_MS : 0;
    const timer = setTimeout(async () => {
      try {
        const result = await api.requestWorkspaceNleHlsPlaylist(workspaceId, {
          orderedCutIds: cutIds,
          fastPacing: !!fastPacing,
          flipbookIntro: !!flipbookIntro,
        });
        const data = result?.data || result;
        if (requestId !== playlistRequestRef.current) return;
        if (applyHlsPayload(data, requestId)) return;
        loadedPlaylistKeyRef.current = playlistKey;
        hlsUriRef.current = '';
        setHlsUri('');
        setPreviewMode('progressive');
        setPreviewRebuilding(false);
      } catch (error) {
        if (requestId !== playlistRequestRef.current) return;
        loadedPlaylistKeyRef.current = '';
        hlsUriRef.current = '';
        setHlsUri('');
        setPreviewMode('progressive');
        setPreviewRebuilding(false);
      }
    }, delayMs);
    return () => {
      clearTimeout(timer);
    };
  }, [open, workspaceId, cuts, fastPacing, flipbookIntro]);

  useEffect(() => {
    if (!open) {
      playlistRequestRef.current += 1;
      loadedPlaylistKeyRef.current = '';
      hlsUriRef.current = '';
      revokeHlsBlob();
      setHlsUri('');
      setPreviewMode('none');
      setPreviewRebuilding(false);
      setIsScrubbing(false);
      isScrubbingRef.current = false;
      stopAllSfx();
    }
  }, [open, stopAllSfx]);

  // Music bed — seek only on play/pause/track change, never every playhead tick.
  useEffect(() => {
    const el = musicRef.current;
    if (!el) return;
    if (!useMusic || !selectedMusic?.url) {
      el.pause();
      return;
    }
    if (el.getAttribute('data-music-url') !== selectedMusic.url) {
      el.src = selectedMusic.url;
      el.setAttribute('data-music-url', selectedMusic.url);
    }
    el.volume = 0.2;
    if (playing && !isScrubbing) {
      const musicTime = playheadRef.current;
      try {
        if (Math.abs((el.currentTime || 0) - musicTime) > 0.35) el.currentTime = musicTime;
      } catch (_) {}
      el.play().catch(() => {});
    } else {
      el.pause();
    }
  }, [useMusic, selectedMusic, playing, isScrubbing]);

  const handlePlayPause = () => {
    const el = voRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      musicRef.current?.pause();
      introSfxRef.current?.pause();
      stopAllSfx();
      playingRef.current = false;
      setPlaying(false);
      syncPlayhead(el.currentTime || playheadRef.current, { forceUi: true });
      return;
    }
    let voTime = playheadRef.current;
    const atEnd = voTime >= totalTimelineSec - 0.08 || el.ended;
    if (atEnd) {
      introSfxPlayedRef.current = false;
      scrollToTime(0, { seekMedia: true });
      voTime = 0;
    }
    stopAllSfx();
    // Exclusive lower bound so a cue at t=0 still crosses on the first tick.
    sfxLastTRef.current = voTime <= 0.001 ? -0.001 : voTime;
    sfxArmedRef.current = new Set();
    try {
      el.currentTime = Math.min(voTime, el.duration || voTime);
    } catch (_) {}
    el.play().catch(() => {});
    playIntroSfxIfNeeded(voTime);
    if (voTime > 0.02) armSfxAtPlayhead(voTime);
    playingRef.current = true;
    setPlaying(true);
  };

  const handleSkipStart = () => {
    voRef.current?.pause();
    musicRef.current?.pause();
    introSfxRef.current?.pause();
    stopAllSfx();
    playingRef.current = false;
    setPlaying(false);
    introSfxPlayedRef.current = false;
    sfxLastTRef.current = 0;
    if (voRef.current) {
      try { voRef.current.currentTime = 0; } catch (_) {}
    }
    scrollToTime(0, { seekMedia: false });
  };

  const timeFromClientX = (clientX) => {
    const scrollEl = tracksScrollRef.current;
    if (!scrollEl) return 0;
    const rect = scrollEl.getBoundingClientRect();
    const contentX = (scrollEl.scrollLeft || 0) + (clientX - rect.left);
    return Math.max(0, Math.min(totalTimelineSec, timelineTimeFromPx(contentX - trackStart)));
  };

  const seekFromEvent = (e) => {
    const t = timeFromClientX(e.clientX);
    beginUserScrub();
    scrollToTime(t, { seekMedia: false });
    finishUserScrub(t);
  };

  const handleTracksScroll = (e) => {
    if (programmaticScrollRef.current) return;
    beginUserScrub();
    if (scrubEndTimerRef.current) clearTimeout(scrubEndTimerRef.current);
    const t = Math.max(0, Math.min(totalTimelineSec, timelineTimeFromPx(e.currentTarget.scrollLeft)));
    playheadRef.current = t;
    const now = Date.now();
    if (now - lastUiPlayheadAtRef.current >= PLAYHEAD_UI_MS) {
      lastUiPlayheadAtRef.current = now;
      setPlayhead(t);
    }
    scrubEndTimerRef.current = setTimeout(() => finishUserScrub(playheadRef.current), SCRUB_SETTLE_MS);
  };

  const removeCut = (index) => {
    setCuts((prev) => {
      const next = prev.filter((_, i) => i !== index);
      if (!next.length) allowAutoSeedRef.current = false;
      return next;
    });
  };

  const addCut = (cut) => {
    const stripTime = playheadRef.current;
    allowAutoSeedRef.current = false;
    setCuts((prev) => {
      const idx = insertCutIndexAtPlayhead(prev, stripTime, fastPacing, flipbookIntro);
      const next = [...prev];
      next.splice(idx, 0, stampCutKey(cut, idx, { force: true }));
      pendingScrollTimeRef.current = playheadJustPastMidCut(next, idx, fastPacing, flipbookIntro);
      return next;
    });
  };
  // Stable identities for the memoized picker grid; always call the latest closures.
  const addCutRef = useRef(addCut);
  addCutRef.current = addCut;
  const onPickerAddCut = useCallback((cut) => addCutRef.current(cut), []);
  const onLoadMoreCutsRef = useRef(onLoadMoreCuts);
  onLoadMoreCutsRef.current = onLoadMoreCuts;
  const onPickerLoadMore = useCallback(() => onLoadMoreCutsRef.current?.(), []);

  const moveCut = (from, to) => {
    setCuts((prev) => {
      if (from < 0 || from >= prev.length || to < 0 || to >= prev.length || from === to) return prev;
      const next = prev.slice();
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  const commitDragReorder = useCallback(() => {
    const pos = positionsRef.current;
    if (!pos.length || draggingIndex < 0) {
      setDraggingIndex(-1);
      return;
    }
    setCuts((prev) => {
      if (pos.length !== prev.length) return prev;
      const next = new Array(prev.length);
      for (let i = 0; i < prev.length; i++) next[pos[i]] = prev[i];
      const changed = next.some((c, i) => c !== prev[i]);
      return changed ? next : prev;
    });
    setDraggingIndex(-1);
  }, [draggingIndex]);

  const randomizeCuts = () => {
    if (!availableCuts?.length) return;
    const need = resolveRequiredSeconds({ voiceoverSeconds: voDuration, flipbookIntro });
    const target = need > 0 ? need : Math.max(sumCutsSeconds(cuts, fastPacing), 8);
    allowAutoSeedRef.current = false;
    setCuts(stampCutKeys(seedCutsToDuration(availableCuts, target, fastPacing), { force: true }));
    setDraggingIndex(-1);
  };

  const emitTagsChange = (inc, exc) => {
    setLocalIncludeTags(inc);
    setLocalExcludeTags(exc);
    if (typeof onTagsChange === 'function') onTagsChange(inc, exc);
  };

  const addIncludeTag = () => {
    const tags = includeTagInput.split(',').map((t) => t.trim()).filter((t) => t && !localIncludeTags.includes(t));
    if (!tags.length) return;
    emitTagsChange([...localIncludeTags, ...tags], localExcludeTags);
    setIncludeTagInput('');
  };

  const addExcludeTag = () => {
    const tags = excludeTagInput.split(',').map((t) => t.trim()).filter((t) => t && !localExcludeTags.includes(t));
    if (!tags.length) return;
    emitTagsChange(localIncludeTags, [...localExcludeTags, ...tags]);
    setExcludeTagInput('');
  };

  const addSfxAt = (sfxMeta, atTime) => {
    const cue = {
      clientId: `sfx-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      id: sfxMeta.id,
      file: sfxMeta.file,
      url: buildSfxUrl(sfxMeta.file),
      startTime: Math.max(0, atTime),
      label: sfxMeta.label || sfxMeta.id,
    };
    setSfxCues((prev) => [...prev, cue].sort((a, b) => a.startTime - b.startTime));
    closeSfxModal();
  };

  const removeSfx = (clientId) => {
    setSfxCues((prev) => prev.filter((c) => c.clientId !== clientId));
  };

  const previewSfx = (sfxMeta) => {
    const el = sfxPreviewRef.current;
    if (!el) return;
    if (previewSfxId === sfxMeta.id) {
      el.pause();
      setPreviewSfxId(null);
      return;
    }
    el.src = buildSfxUrl(sfxMeta.file);
    el.currentTime = 0;
    el.play().catch(() => {});
    setPreviewSfxId(sfxMeta.id);
    el.onended = () => setPreviewSfxId(null);
  };

  const previewMusic = (track) => {
    const el = musicPreviewRef.current;
    if (!el || !track?.url) return;
    if (previewMusicId === track._id) {
      el.pause();
      setPreviewMusicId(null);
      return;
    }
    el.src = track.url;
    el.currentTime = 0;
    el.volume = 0.7;
    el.play().catch(() => {});
    setPreviewMusicId(track._id);
    el.onended = () => setPreviewMusicId(null);
  };

  const closeMusicModal = () => {
    musicPreviewRef.current?.pause();
    setPreviewMusicId(null);
    setShowMusicModal(false);
  };

  const closeSfxModal = () => {
    sfxPreviewRef.current?.pause();
    setPreviewSfxId(null);
    setShowSfxModal(false);
  };

  const handleAutoSfx = async () => {
    if (!onRequestAutoSfx) return;
    const cues = await onRequestAutoSfx();
    if (!Array.isArray(cues) || !cues.length) return;
    const normalized = normalizeClientSfxCues(cues).map((c, i) => ({
      ...c,
      clientId: `auto-${i}-${Date.now()}`,
      label: SFX_CATALOG.find((s) => s.id === c.id)?.label || c.id,
    }));
    setSfxCues(normalized);
  };

  const handleExport = () => {
    if (!onExport) return;
    let exportCuts = cuts;
    if (requiredSec > 0 && sumCutsSeconds(cuts, fastPacing) < requiredSec && availableCuts?.length) {
      exportCuts = stampCutKeys(appendCutsToDuration(cuts, availableCuts, requiredSec, fastPacing));
      setCuts(exportCuts);
      allowAutoSeedRef.current = false;
    }
    if (!exportCuts.length) return;
    onExport({
      orderedCutIds: exportCuts.map((c) => c._id || c.id).filter(Boolean),
      selectedMusicId: useMusic && musicId ? musicId : null,
      flipbookIntro,
      cutSilenceMode: isBoukar && cutSilence,
      fastPacing,
      sfxCues: normalizeClientSfxCues(sfxCues),
      enableSubtitles,
      oneWordPerSubtitle,
      subtitlePosition,
      bigText,
      glowText: false,
      fontId: fontId || null,
      subtitleFontColor: fontColor,
      subtitleOutlineColor: outlineColor,
      subtitleOutlineEnabled: outlineEnabled,
      openingLineInCenter: isBoukar && titleCard,
      openingLineTextSize: titleTextSize,
      openingLineFontId: isBoukar && titleCard ? (titleFontId || null) : null,
      openingLineFontColor: isBoukar && titleCard ? titleFontColor : undefined,
      fontMixing: isBoukar && fontMixing,
      centerStackOpening: isBoukar && centerStackOpening,
      centerStackLineByLine: isBoukar && centerStackLineByLine,
      fontMixingSecondColor: secondColor,
    });
  };

  const cutsSec = sumCutsSeconds(cuts, fastPacing);
  const countOk = requiredSec <= 0 || cutsSec >= requiredSec;

  const cutsFilterAndGrid = (
    <>
      <div className="new-nle__cuts-filter">
        <button
          type="button"
          className={`new-nle__btn new-nle__btn--small${showCutsFilter ? ' new-nle__btn--on' : ''}`}
          onClick={() => setShowCutsFilter((v) => !v)}
        >
          Filter cuts
          {(localIncludeTags.length + localExcludeTags.length) > 0 && (
            <span className="new-nle__filter-count">
              {localIncludeTags.length + localExcludeTags.length}
            </span>
          )}
        </button>
        {showCutsFilter && (
          <div className="new-nle__tags-row">
            <div className="new-nle__tags-group">
              <span className="new-nle__subs-label">Include tags</span>
              <div className="new-nle__tags-chips">
                {localIncludeTags.map((tag) => (
                  <button
                    key={`inc-${tag}`}
                    type="button"
                    className="new-nle__tag-chip"
                    onClick={() => emitTagsChange(localIncludeTags.filter((t) => t !== tag), localExcludeTags)}
                  >
                    {tag} ×
                  </button>
                ))}
                <div className="new-nle__tag-suggest-wrap">
                  <input
                    className="new-nle__tag-input"
                    value={includeTagInput}
                    placeholder="Add…"
                    onChange={(e) => {
                      setIncludeTagInput(e.target.value);
                      setShowIncludeSuggestions(true);
                    }}
                    onFocus={() => setShowIncludeSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowIncludeSuggestions(false), 180)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addIncludeTag(); setShowIncludeSuggestions(false); } }}
                  />
                  {showIncludeSuggestions && filterTagSuggestions(includeTagInput, localIncludeTags).length > 0 && (
                    <div className="new-nle__tag-suggest">
                      {filterTagSuggestions(includeTagInput, localIncludeTags).map((tag) => (
                        <button
                          key={`inc-sug-${tag}`}
                          type="button"
                          className="new-nle__tag-suggest-item"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            emitTagsChange([...localIncludeTags, tag], localExcludeTags);
                            setIncludeTagInput('');
                            setShowIncludeSuggestions(false);
                          }}
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button type="button" className="new-nle__btn new-nle__btn--small" onClick={addIncludeTag}>Add</button>
              </div>
            </div>
            <div className="new-nle__tags-group">
              <span className="new-nle__subs-label">Exclude tags</span>
              <div className="new-nle__tags-chips">
                {localExcludeTags.map((tag) => (
                  <button
                    key={`exc-${tag}`}
                    type="button"
                    className="new-nle__tag-chip new-nle__tag-chip--exclude"
                    onClick={() => emitTagsChange(localIncludeTags, localExcludeTags.filter((t) => t !== tag))}
                  >
                    {tag} ×
                  </button>
                ))}
                <div className="new-nle__tag-suggest-wrap">
                  <input
                    className="new-nle__tag-input"
                    value={excludeTagInput}
                    placeholder="Add…"
                    onChange={(e) => {
                      setExcludeTagInput(e.target.value);
                      setShowExcludeSuggestions(true);
                    }}
                    onFocus={() => setShowExcludeSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowExcludeSuggestions(false), 180)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addExcludeTag(); setShowExcludeSuggestions(false); } }}
                  />
                  {showExcludeSuggestions && filterTagSuggestions(excludeTagInput, localExcludeTags).length > 0 && (
                    <div className="new-nle__tag-suggest">
                      {filterTagSuggestions(excludeTagInput, localExcludeTags).map((tag) => (
                        <button
                          key={`exc-sug-${tag}`}
                          type="button"
                          className="new-nle__tag-suggest-item"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            emitTagsChange(localIncludeTags, [...localExcludeTags, tag]);
                            setExcludeTagInput('');
                            setShowExcludeSuggestions(false);
                          }}
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button type="button" className="new-nle__btn new-nle__btn--small" onClick={addExcludeTag}>Add</button>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="new-nle__cuts-grid-wrap">
      <CutsPickerGrid
        availableCuts={availableCuts}
        cutUsageById={cutUsageById}
        fastPacing={fastPacing}
        hasMoreCuts={hasMoreCuts}
        loadingMoreCuts={loadingMoreCuts}
        onLoadMore={onPickerLoadMore}
        onAdd={onPickerAddCut}
      />
      <div className="new-nle__cuts-grid-fade" aria-hidden="true" />
      </div>
    </>
  );

  // --- Drag coalescing: pointermove fires faster than the display refresh; apply at most one
  // state update per frame (latest position wins) and flush synchronously on release.
  const dragPendingRef = useRef(null);
  const dragRafRef = useRef(0);
  const sfxDragPendingRef = useRef(null);
  const sfxDragRafRef = useRef(0);

  const flushCutDrag = useCallback(() => {
    if (dragRafRef.current) {
      cancelAnimationFrame(dragRafRef.current);
      dragRafRef.current = 0;
    }
    const p = dragPendingRef.current;
    if (!p) return;
    dragPendingRef.current = null;
    const prev = positionsRef.current;
    const target = slotForCenterX(p.x - p.base + p.w / 2, prev, p.cardWidths);
    const current = prev[p.i];
    if (target !== current) {
      const next = movePosition(prev, current, target);
      positionsRef.current = next;
      setPositions(next);
    }
    setDragX(p.x);
  }, []);

  const flushSfxDrag = useCallback(() => {
    if (sfxDragRafRef.current) {
      cancelAnimationFrame(sfxDragRafRef.current);
      sfxDragRafRef.current = 0;
    }
    const p = sfxDragPendingRef.current;
    if (!p) return;
    sfxDragPendingRef.current = null;
    setSfxCues((prev) =>
      prev
        .map((c) => (c.clientId === p.clientId ? { ...c, startTime: p.t } : c))
        .sort((a, b) => a.startTime - b.startTime)
    );
  }, []);

  useEffect(() => () => {
    if (dragRafRef.current) cancelAnimationFrame(dragRafRef.current);
    if (sfxDragRafRef.current) cancelAnimationFrame(sfxDragRafRef.current);
  }, []);

  // Stable forwarders for memoized <CutClip>; the real handlers below are re-assigned
  // every render so they always see the latest state (same behavior as inline handlers).
  const clipHandlersRef = useRef(null);
  const clipHandlers = useMemo(() => ({
    onPointerDown: (i, e) => clipHandlersRef.current?.onPointerDown(i, e),
    onPointerMove: (i, e) => clipHandlersRef.current?.onPointerMove(i, e),
    onPointerUp: (i, e) => clipHandlersRef.current?.onPointerUp(i, e),
    onPointerCancel: (i, e) => clipHandlersRef.current?.onPointerCancel(i, e),
    onRemove: (i) => clipHandlersRef.current?.onRemove(i),
  }), []);

  if (!open) return null;

  const introOffsetPx = flipbookIntro ? FLIPBOOK_INTRO_DURATION_SEC * NEW_NLE_PX_PER_SEC : 0;
  // Bucketed visible window: ruler + subtitle lane only render what is on screen.
  const timelineWin = timelineWindowSec(playhead, viewportW, TIMELINE_OVERSCAN_PX);

  const cutWidthAt = (i) => (
    cardWidths[i] || Math.max(8, cutEffectiveSeconds(cuts[i], fastPacing) * NEW_NLE_PX_PER_SEC - CUT_GAP_PX)
  );
  const endClipPress = (e, i) => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
    if (draggingIndex === i) {
      flushCutDrag();
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
      userScrubbingRef.current = false;
      commitDragReorder();
    }
  };
  clipHandlersRef.current = {
    onPointerDown: (i, e) => {
      if (e.button !== 0 || activeTrack !== 'video') return;
      e.stopPropagation();
      if (pressTimerRef.current) clearTimeout(pressTimerRef.current);
      const target = e.currentTarget;
      const pointerId = e.pointerId;
      const clientX = e.clientX;
      pressTimerRef.current = setTimeout(() => {
        pressTimerRef.current = null;
        const startX = trackStart + introOffsetPx + offsetForItem(i, positionsRef.current, slotPrefix);
        dragStartXRef.current = startX;
        dragStartScrollRef.current = tracksScrollRef.current?.scrollLeft ?? 0;
        dragStartClientXRef.current = clientX;
        setDragX(startX);
        setDraggingIndex(i);
        userScrubbingRef.current = true;
        if (target?.isConnected) {
          try { target.setPointerCapture(pointerId); } catch (_) {}
        }
      }, LONG_PRESS_MS);
    },
    onPointerMove: (i, e) => {
      if (draggingIndex !== i) return;
      const w = cutWidthAt(i);
      const scrollEl = tracksScrollRef.current;
      const curScroll = scrollEl?.scrollLeft ?? 0;
      const deltaX = e.clientX - dragStartClientXRef.current + (curScroll - dragStartScrollRef.current);
      const x = dragStartXRef.current + deltaX;
      dragPendingRef.current = { i, x, w, cardWidths, base: trackStart + introOffsetPx };
      if (!dragRafRef.current) dragRafRef.current = requestAnimationFrame(flushCutDrag);
    },
    onPointerUp: (i, e) => endClipPress(e, i),
    onPointerCancel: (i, e) => endClipPress(e, i),
    onRemove: (i) => {
      if (draggingIndex >= 0) return;
      removeCut(i);
    },
  };

  return (
    <div className={`new-nle-overlay${!darkMode ? ' new-nle-overlay--light' : ''}`} onClick={onClose}>
      <div className={`new-nle${!darkMode ? ' new-nle--light' : ''}`} onClick={(e) => e.stopPropagation()}>
        <header className="new-nle__header">
          <div className="new-nle__header-left">
            <h2>Timeline</h2>
            <span className="new-nle__meta">
              {formatNleTime(playhead)} / {formatNleTime(totalTimelineSec)}
              {requiredSec > 0 && (
                <span className={countOk ? '' : ' new-nle__meta--warn'}>
                  {' · '}{cutsSec}/{requiredSec}s cuts
                </span>
              )}
            </span>
          </div>
          <div className="new-nle__header-actions">
            <div className="new-nle__cut-length" title="Seconds per cut">
              <span className="new-nle__cut-length-label">Cut length</span>
              <button
                type="button"
                className={`new-nle__btn new-nle__btn--small${fastPacing ? ' new-nle__btn--on' : ''}`}
                onClick={() => setFastPacing(true)}
              >
                1 sec
              </button>
              <button
                type="button"
                className={`new-nle__btn new-nle__btn--small${!fastPacing ? ' new-nle__btn--on' : ''}`}
                onClick={() => setFastPacing(false)}
              >
                2 sec
              </button>
            </div>
            {isBoukar ? (
              <button
                type="button"
                className={`new-nle__btn${cutSilence ? ' new-nle__btn--on' : ''}`}
                onClick={() => setCutSilence((v) => !v)}
                title="Cut silence in voiceover on export"
              >
                <Scissors size={14} />
                Cut silence
              </button>
            ) : null}
            <button
              type="button"
              className={`new-nle__btn${flipbookIntro ? ' new-nle__btn--on' : ''}`}
              onClick={() => setFlipbookIntro((v) => !v)}
            >
              Fast-cut intro
            </button>
            <button
              type="button"
              className="new-nle__btn new-nle__btn--primary"
              disabled={!cuts.length || exporting}
              title={countOk ? 'Generate video with this timeline' : 'Generate — missing duration will be filled with random cuts (tags/sources respected)'}
              onClick={handleExport}
            >
              {exporting ? 'Exporting…' : 'Export'}
            </button>
            <button type="button" className="new-nle__icon-btn" onClick={onClose} aria-label="Close">
              <X size={18} />
            </button>
          </div>
        </header>

        <div className="new-nle__body">
          <div className="new-nle__timeline-wrap">
            <div className="new-nle__track-labels">
              <div className="new-nle__track-label new-nle__track-label--ruler" style={{ height: NLE_RULER_H, minHeight: NLE_RULER_H, flex: 'none' }} aria-hidden />
              <TrackLabel id="video" active={activeTrack === 'video'} height={NLE_LANE_VIDEO} onClick={() => setActiveTrack('video')}>
                <Film size={12} /> Video
              </TrackLabel>
              <TrackLabel id="voiceover" active={activeTrack === 'voiceover'} height={NLE_LANE_AUDIO} onClick={() => setActiveTrack('voiceover')}>
                <Volume2 size={12} /> VO
              </TrackLabel>
              <TrackLabel id="music" active={activeTrack === 'music'} height={NLE_LANE_AUDIO} onClick={() => { setActiveTrack('music'); setShowMusicModal(true); }}>
                <Music size={12} /> Music
              </TrackLabel>
              <TrackLabel id="sfx" active={activeTrack === 'sfx'} height={NLE_LANE_SFX} onClick={() => setActiveTrack('sfx')}>
                <Volume2 size={12} /> SFX
              </TrackLabel>
              <TrackLabel id="subtitles" active={activeTrack === 'subtitles'} height={NLE_LANE_SUB} onClick={() => setActiveTrack('subtitles')}>
                <Type size={12} /> Subs
              </TrackLabel>
            </div>

            <div className="new-nle__tracks-viewport" ref={tracksViewportRef}>
              <div className="new-nle__playhead" />
              <div
                className="new-nle__tracks-scroll"
                ref={tracksScrollRef}
                onScroll={handleTracksScroll}
                onPointerDown={(e) => {
                  if (e.target.closest?.('button, input, textarea')) return;
                  beginUserScrub();
                }}
                onPointerUp={() => {
                  if (!isScrubbingRef.current) return;
                  if (scrubEndTimerRef.current) return;
                  finishUserScrub(playheadRef.current);
                }}
                onPointerCancel={() => {
                  if (!isScrubbingRef.current) return;
                  finishUserScrub(playheadRef.current);
                }}
              >
                <div className="new-nle__tracks" style={{ width: contentWidth }}>
                {/* Time ruler */}
                <TimeRuler
                  totalSec={totalTimelineSec}
                  trackStart={trackStart}
                  fromSec={timelineWin.fromSec}
                  toSec={timelineWin.toSec}
                />

                {/* Video: fast-cut intro (0–1.8s) then cuts */}
                <div
                  className={`new-nle__lane${activeTrack === 'video' ? ' new-nle__lane--active' : ''}`}
                  style={{ height: NLE_LANE_VIDEO, flex: 'none', minHeight: NLE_LANE_VIDEO }}
                  onClick={(e) => { setActiveTrack('video'); seekFromEvent(e); }}
                >
                  <button
                    type="button"
                    className="new-nle__randomize-btn new-nle__randomize-btn--start"
                    style={{ left: Math.max(8, trackStart - 36) }}
                    title="randomize cuts"
                    onClick={(e) => { e.stopPropagation(); randomizeCuts(); }}
                  >
                    <Shuffle size={12} />
                  </button>
                  {flipbookIntro && (
                    <div
                      className="new-nle__clip new-nle__clip--intro"
                      style={{ left: trackStart, width: FLIPBOOK_INTRO_DURATION_SEC * NEW_NLE_PX_PER_SEC }}
                      title="Fast-cut intro (1.8s)"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveTrack('video');
                        scrollToTime(0);
                      }}
                    >
                      Fast-cut intro
                    </div>
                  )}
                  {visibleCutIndexes.map((i) => {
                    const cut = cuts[i];
                    if (!cut) return null;
                    const sec = cutEffectiveSeconds(cut, fastPacing);
                    const w = cardWidths[i] || Math.max(8, sec * NEW_NLE_PX_PER_SEC - CUT_GAP_PX);
                    const restX = trackStart + introOffsetPx + offsetForItem(i, positions, slotPrefix);
                    const isDragging = draggingIndex === i;
                    const translateX = isDragging ? dragX : restX;
                    return (
                      <CutClip
                        key={cut._nleKey || `${cut._id || cut.id}-${i}`}
                        cut={cut}
                        index={i}
                        sec={sec}
                        width={w}
                        translateX={translateX}
                        isDragging={isDragging}
                        suppressTransition={suppressTransition}
                        eager={i < 8}
                        handlers={clipHandlers}
                      />
                    );
                  })}
                  <button
                    type="button"
                    className="new-nle__add-cuts-empty"
                    style={{
                      left: trackStart + introOffsetPx + (
                        cuts.length === 0
                          ? 0
                          : sumCutsSeconds(cuts, fastPacing) * NEW_NLE_PX_PER_SEC + 8
                      ),
                    }}
                    title="Add cuts"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveTrack('video');
                      setShowCutsPicker(true);
                    }}
                  >
                    <Plus size={18} />
                  </button>
                </div>

                {/* Voiceover — waveform like legacy */}
                <div
                  className={`new-nle__lane new-nle__lane--vo${activeTrack === 'voiceover' ? ' new-nle__lane--active' : ''}`}
                  style={{ height: NLE_LANE_AUDIO, flex: 'none', minHeight: NLE_LANE_AUDIO }}
                  onClick={(e) => { setActiveTrack('voiceover'); seekFromEvent(e); }}
                >
                  <div
                    className="new-nle__clip new-nle__clip--audio-lane"
                    style={{
                      left: trackStart,
                      width: Math.max(8, (voDuration || totalTimelineSec) * NEW_NLE_PX_PER_SEC),
                    }}
                  >
                    <FakeWaveform width={Math.max(8, (voDuration || totalTimelineSec) * NEW_NLE_PX_PER_SEC)} height={28} />
                  </div>
                </div>

                {/* Music */}
                <div
                  className={`new-nle__lane${activeTrack === 'music' ? ' new-nle__lane--active' : ''}`}
                  style={{ height: NLE_LANE_AUDIO, flex: 'none', minHeight: NLE_LANE_AUDIO }}
                  onClick={(e) => {
                    setActiveTrack('music');
                    seekFromEvent(e);
                    setShowMusicModal(true);
                  }}
                >
                  {useMusic && selectedMusic ? (
                    <div
                      className="new-nle__clip new-nle__clip--audio-lane new-nle__clip--music-wave"
                      style={{ left: trackStart, width: trackWidth }}
                      title={`${selectedMusic.name || 'Music'} · tap to change`}
                    >
                      <FakeWaveform width={trackWidth} height={28} />
                      <button
                        type="button"
                        className="new-nle__clip-remove"
                        title="Remove music"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          setUseMusic(false);
                          setMusicId('');
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <div className="new-nle__lane-cta" style={{ left: trackStart }}>+ Add song</div>
                  )}
                </div>

                {/* SFX */}
                <div
                  className={`new-nle__lane${activeTrack === 'sfx' ? ' new-nle__lane--active' : ''}`}
                  style={{ height: NLE_LANE_SFX, flex: 'none', minHeight: NLE_LANE_SFX }}
                  onClick={(e) => {
                    setActiveTrack('sfx');
                    const t = timeFromClientX(e.clientX);
                    scrollToTime(t);
                    setSfxModalTime(t);
                    setShowSfxModal(true);
                  }}
                >
                  {sfxCues.map((cue) => (
                    <div
                      key={cue.clientId}
                      className={`new-nle__sfx-marker${draggingSfxId === cue.clientId ? ' new-nle__sfx-marker--dragging' : ''}`}
                      style={{
                        left: trackStart + cue.startTime * NEW_NLE_PX_PER_SEC,
                        width: Math.max(28, sfxCueDuration(cue) * NEW_NLE_PX_PER_SEC),
                      }}
                      title={`${cue.label || cue.id} @ ${formatNleTime(cue.startTime)}`}
                      onClick={(e) => e.stopPropagation()}
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        e.currentTarget.setPointerCapture(e.pointerId);
                        setDraggingSfxId(cue.clientId);
                        wasPlayingRef.current = playing;
                        userScrubbingRef.current = true;
                        stopAllSfx();
                        if (playing) {
                          voRef.current?.pause();
                          musicRef.current?.pause();
                          playingRef.current = false;
                          setPlaying(false);
                        }
                      }}
                      onPointerMove={(e) => {
                        if (draggingSfxId !== cue.clientId) return;
                        // Coalesce to one state update per frame; latest pointer position wins.
                        sfxDragPendingRef.current = { clientId: cue.clientId, t: timeFromClientX(e.clientX) };
                        if (!sfxDragRafRef.current) sfxDragRafRef.current = requestAnimationFrame(flushSfxDrag);
                      }}
                      onPointerUp={() => {
                        flushSfxDrag();
                        setDraggingSfxId(null);
                        userScrubbingRef.current = false;
                      }}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        removeSfx(cue.clientId);
                      }}
                    >
                      <span className="new-nle__sfx-marker-label">{cue.label || cue.id}</span>
                      <button
                        type="button"
                        className="new-nle__sfx-marker-remove"
                        title="Remove SFX"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          removeSfx(cue.clientId);
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <div className="new-nle__sfx-actions" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="new-nle__btn new-nle__btn--small"
                      onClick={() => {
                        setSfxModalTime(playhead);
                        setShowSfxModal(true);
                      }}
                    >
                      <Plus size={12} /> Add SFX
                    </button>
                    <button
                      type="button"
                      className="new-nle__btn new-nle__btn--small"
                      disabled={planningSfx}
                      onClick={handleAutoSfx}
                    >
                      {planningSfx ? 'Planning…' : 'Auto SFX'}
                    </button>
                  </div>
                </div>

                {/* Subtitles */}
                <div
                  className={`new-nle__lane${activeTrack === 'subtitles' ? ' new-nle__lane--active' : ''}`}
                  style={{ height: NLE_LANE_SUB, flex: 'none', minHeight: NLE_LANE_SUB }}
                  onClick={(e) => { setActiveTrack('subtitles'); seekFromEvent(e); }}
                >
                  {enableSubtitles ? (
                    <SubtitleLane
                      blocks={subtitles}
                      trackStart={trackStart}
                      fromSec={timelineWin.fromSec}
                      toSec={timelineWin.toSec}
                    />
                  ) : (
                    <div className="new-nle__lane-empty" style={{ left: trackStart }}>Subtitles off — click Subs for options</div>
                  )}
                </div>
                </div>
              </div>
            </div>
          </div>

          <div className="new-nle__preview">
            <div className="new-nle__preview-frame">
              {cutPosterUrl(activePreview.cut) ? (
                <img
                  className="new-nle__preview-poster"
                  src={cutPosterUrl(activePreview.cut)}
                  alt=""
                />
              ) : null}
              {cutPreviewUrl(activePreview.cut) ? (
                previewMode === 'hls' && hlsUri ? (
                  <NleHlsPreview
                    uri={hlsUri}
                    playing={playing}
                    playhead={playhead}
                    scrubbing={isScrubbing}
                    className={`new-nle__preview-video${activePreview.kind === 'intro' ? ' new-nle__preview-video--flash' : ''}`}
                    onError={() => {
                      hlsUriRef.current = '';
                      setHlsUri('');
                      setPreviewMode('progressive');
                      setPreviewRebuilding(false);
                    }}
                    onReady={() => setPreviewRebuilding(false)}
                  />
                ) : (
                  <NleProgressivePreview
                    cuts={cuts}
                    playhead={playhead}
                    playing={playing}
                    scrubbing={isScrubbing}
                    fastPacing={fastPacing}
                    flipbookIntro={flipbookIntro}
                    flashPool={flashPool}
                    activeSlot={activePreview}
                    className={`new-nle__preview-video${activePreview.kind === 'intro' ? ' new-nle__preview-video--flash' : ''}`}
                  />
                )
              ) : (
                <div className="new-nle__intro-preview">{loading ? 'Loading…' : 'Add cuts to preview'}</div>
              )}
              {previewRebuilding && cutPreviewUrl(activePreview.cut) ? (
                <div className="new-nle__preview-updating">Updating…</div>
              ) : null}
              {enableSubtitles && (() => {
                const showTitle = isBoukar && titleCard && playhead < titleCardEndSec;
                if (showTitle) {
                  const sizeClass = titleTextSize === 'small' ? 'new-nle__title-overlay--small' : 'new-nle__title-overlay--big';
                  const fontFamily = selectedTitleFont?.name ? `"${selectedTitleFont.name}", sans-serif` : undefined;
                  if (titleCardStyle === 'style1' || titleCardStyle === 'style2' || titleCardStyle === 'style3') {
                    return (
                      <TitleCardLivePreview
                        styleId={titleCardStyle}
                        words={titleTimedWords}
                        playhead={playhead}
                        primaryColor={titleFontColor}
                        accentColor={secondColor}
                        fontFamily={fontFamily}
                        sizeClass={sizeClass}
                      />
                    );
                  }
                  return (
                    <div
                      className={`new-nle__title-overlay ${sizeClass}`}
                      style={{
                        color: titleFontColor || '#ffffff',
                        fontFamily,
                      }}
                    >
                      {titleCardText}
                    </div>
                  );
                }
                const block = subtitles.find((s) => playhead >= s.start && playhead < s.end);
                if (!block?.text) return null;
                const cls = [
                  'new-nle__subtitle-overlay',
                  subtitlePosition === 'center' ? 'new-nle__subtitle-overlay--center' : 'new-nle__subtitle-overlay--bottom',
                  bigText ? 'new-nle__subtitle-overlay--big' : '',
                ].filter(Boolean).join(' ');
                return (
                  <div
                    className={cls}
                    style={previewSubtitleStyle}
                  >
                    {block.text}
                  </div>
                );
              })()}
            </div>
            <div className="new-nle__transport">
              <button type="button" className="new-nle__icon-btn" onClick={handleSkipStart} aria-label="Skip to start">
                <SkipBack size={16} />
              </button>
              <button type="button" className="new-nle__icon-btn" onClick={handlePlayPause} aria-label="Play/Pause">
                {playing ? <Pause size={16} /> : <Play size={16} />}
              </button>
            </div>
            {voiceoverUrl && (
              <audio
                ref={voRef}
                src={voiceoverUrl}
                preload="metadata"
                onLoadedMetadata={(e) => {
                  const d = Number(e.currentTarget.duration);
                  if (Number.isFinite(d) && d > 0) setMeasuredVoSec(d);
                }}
                onDurationChange={(e) => {
                  const d = Number(e.currentTarget.duration);
                  if (Number.isFinite(d) && d > 0) setMeasuredVoSec(d);
                }}
                onEnded={() => {
                  playingRef.current = false;
                  setPlaying(false);
                  introSfxPlayedRef.current = false;
                  introSfxRef.current?.pause();
                  stopAllSfx();
                  syncPlayhead(totalTimelineSec, { forceUi: true });
                }}
              />
            )}
            <audio ref={musicRef} preload="metadata" />
            <audio ref={musicPreviewRef} preload="auto" />
            <audio ref={sfxPreviewRef} preload="auto" />
            <audio ref={introSfxRef} src={RAPID_SHUTTER_URL} preload="auto" />
          </div>

        </div>

        {/* Cuts edit panel — desktop: when video track active; narrow: only after + */}
        {((!isNarrow && activeTrack === 'video') || (isNarrow && showCutsPicker)) && (
          isNarrow ? (
            <div className="new-nle-modal" onClick={() => setShowCutsPicker(false)}>
              <div
                className="new-nle-modal__card new-nle-modal__card--cuts new-nle__cuts-panel"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="new-nle-modal__head">
                  <h3>Add cuts</h3>
                  <button type="button" className="new-nle__icon-btn" onClick={() => setShowCutsPicker(false)} aria-label="Close">
                    <X size={16} />
                  </button>
                </div>
                {cutsFilterAndGrid}
              </div>
            </div>
          ) : (
            <div className="new-nle__cuts-panel">
              <div className="new-nle__cuts-panel-head">
                <strong>Edit cuts</strong>
                <span>
                  {flipbookIntro
                    ? 'Adds insert at the playhead (after the fast-cut intro). VO & subs start with the intro.'
                    : 'Scroll to scrub. New cuts insert at the centered playhead.'}
                </span>
              </div>
              {cutsFilterAndGrid}
            </div>
          )
        )}

        {/* Subtitle + Title Card options when Subs track active */}
        {activeTrack === 'subtitles' && (
          <div
            className={isNarrow ? 'new-nle-modal' : undefined}
            onClick={isNarrow ? () => setActiveTrack('video') : undefined}
          >
            <div
              className={isNarrow
                ? 'new-nle-modal__card new-nle-modal__card--subs new-nle__cuts-panel new-nle__subs-panel'
                : 'new-nle__cuts-panel new-nle__subs-panel'}
              onClick={isNarrow ? (e) => e.stopPropagation() : undefined}
            >
              {isNarrow ? (
                <div className="new-nle-modal__head">
                  <h3>Text options</h3>
                  <button type="button" className="new-nle__icon-btn" onClick={() => setActiveTrack('video')} aria-label="Close">
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="new-nle__cuts-panel-head">
                  <strong>Text options</strong>
                  <span>Live preview + export</span>
                </div>
              )}
              <div className="new-nle__subs-layout">
              <div className="new-nle__subs-col">
                <div className="new-nle__subs-col-title">Subtitles</div>
                <div className="new-nle__subs-options">
                  <label className="new-nle__subs-option">
                    <input
                      type="checkbox"
                      checked={enableSubtitles}
                      onChange={(e) => setEnableSubtitles(e.target.checked)}
                    />
                    Enable subtitles
                  </label>
                  {enableSubtitles && (
                    <>
                      <div className="new-nle__subs-group">
                        <span className="new-nle__subs-label">Font</span>
                        <select
                          className="new-nle__subs-select"
                          value={fontId || ''}
                          onChange={(e) => setFontId(e.target.value)}
                          style={selectedFont?.name ? { fontFamily: `"${selectedFont.name}", sans-serif` } : undefined}
                        >
                          <option value="">Default</option>
                          {studioFonts.map((f) => (
                            <option key={f._id} value={f._id} style={f.name ? { fontFamily: `"${f.name}", sans-serif` } : undefined}>
                              {f.name || 'Untitled'}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="new-nle__subs-group">
                        <span className="new-nle__subs-label">Color</span>
                        <input
                          type="color"
                          className="new-nle__subs-color"
                          value={/^#[0-9a-fA-F]{6}$/.test(fontColor) ? fontColor : '#ffffff'}
                          onChange={(e) => setFontColor(e.target.value)}
                        />
                        <input
                          type="text"
                          className="new-nle__subs-hex"
                          value={fontColor}
                          onChange={(e) => setFontColor(e.target.value)}
                        />
                      </div>
                      <label className="new-nle__subs-option">
                        <input
                          type="checkbox"
                          checked={outlineEnabled}
                          onChange={(e) => setOutlineEnabled(e.target.checked)}
                        />
                        Outline
                      </label>
                      {outlineEnabled && (
                        <div className="new-nle__subs-group">
                          <span className="new-nle__subs-label">Outline</span>
                          <input
                            type="color"
                            className="new-nle__subs-color"
                            value={/^#[0-9a-fA-F]{6}$/.test(outlineColor) ? outlineColor : '#000000'}
                            onChange={(e) => setOutlineColor(e.target.value)}
                          />
                          <input
                            type="text"
                            className="new-nle__subs-hex"
                            value={outlineColor}
                            onChange={(e) => setOutlineColor(e.target.value)}
                          />
                        </div>
                      )}
                      <div className="new-nle__subs-group">
                        <span className="new-nle__subs-label">Position</span>
                        <div className="new-nle__subs-chips">
                          <button
                            type="button"
                            className={`new-nle__btn new-nle__btn--small${subtitlePosition === 'bottom' ? ' new-nle__btn--on' : ''}`}
                            onClick={() => setSubtitlePosition('bottom')}
                          >
                            Bottom
                          </button>
                          <button
                            type="button"
                            className={`new-nle__btn new-nle__btn--small${subtitlePosition === 'center' ? ' new-nle__btn--on' : ''}`}
                            onClick={() => setSubtitlePosition('center')}
                          >
                            Center
                          </button>
                        </div>
                      </div>
                      <div className="new-nle__subs-group">
                        <span className="new-nle__subs-label">Text size</span>
                        <div className="new-nle__subs-chips">
                          <button
                            type="button"
                            className={`new-nle__btn new-nle__btn--small${!bigText ? ' new-nle__btn--on' : ''}`}
                            onClick={() => setBigText(false)}
                          >
                            Normal
                          </button>
                          <button
                            type="button"
                            className={`new-nle__btn new-nle__btn--small${bigText ? ' new-nle__btn--on' : ''}`}
                            onClick={() => setBigText(true)}
                          >
                            Big
                          </button>
                        </div>
                      </div>
                      <label className="new-nle__subs-option">
                        <input
                          type="checkbox"
                          checked={oneWordPerSubtitle}
                          onChange={(e) => setOneWordPerSubtitle(e.target.checked)}
                        />
                        1 word per subtitle
                      </label>
                    </>
                  )}
                </div>
              </div>

              <div className="new-nle__subs-col new-nle__subs-col--title-card">
                <div className="new-nle__subs-col-title">Title card</div>
                {isBoukar ? (
                <div className="new-nle__subs-options">
                  <label className="new-nle__subs-option">
                    <input
                      type="checkbox"
                      checked={titleCard}
                      disabled={!enableSubtitles}
                      onChange={(e) => setTitleCard(e.target.checked)}
                    />
                    Enable title card
                  </label>
                  <span className="new-nle__subs-hint">First sentence becomes the title</span>
                  {enableSubtitles && titleCard && (
                    <>
                      <div className="new-nle__subs-group">
                        <span className="new-nle__subs-label">Size</span>
                        <div className="new-nle__subs-chips">
                          <button
                            type="button"
                            className={`new-nle__btn new-nle__btn--small${titleTextSize === 'big' ? ' new-nle__btn--on' : ''}`}
                            onClick={() => setTitleTextSize('big')}
                          >
                            Big
                          </button>
                          <button
                            type="button"
                            className={`new-nle__btn new-nle__btn--small${titleTextSize === 'small' ? ' new-nle__btn--on' : ''}`}
                            onClick={() => setTitleTextSize('small')}
                          >
                            Small
                          </button>
                        </div>
                      </div>
                      <div className="new-nle__subs-group">
                        <span className="new-nle__subs-label">Font</span>
                        <select
                          className="new-nle__subs-select"
                          value={titleFontId || ''}
                          onChange={(e) => setTitleFontId(e.target.value)}
                          style={selectedTitleFont?.name ? { fontFamily: `"${selectedTitleFont.name}", sans-serif` } : undefined}
                        >
                          <option value="">Default</option>
                          {studioFonts.map((f) => (
                            <option key={`title-${f._id}`} value={f._id} style={f.name ? { fontFamily: `"${f.name}", sans-serif` } : undefined}>
                              {f.name || 'Untitled'}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="new-nle__subs-group">
                        <span className="new-nle__subs-label">Color</span>
                        <input
                          type="color"
                          className="new-nle__subs-color"
                          value={/^#[0-9a-fA-F]{6}$/.test(titleFontColor) ? titleFontColor : '#ffffff'}
                          onChange={(e) => setTitleFontColor(e.target.value)}
                        />
                        <input
                          type="text"
                          className="new-nle__subs-hex"
                          value={titleFontColor}
                          onChange={(e) => setTitleFontColor(e.target.value)}
                        />
                      </div>
                      <div className="new-nle__subs-group">
                        <span className="new-nle__subs-label">Style</span>
                        <div className="new-nle__subs-chips">
                          <button
                            type="button"
                            className={`new-nle__btn new-nle__btn--small${titleCardStyle === 'standard' ? ' new-nle__btn--on' : ''}`}
                            onClick={() => setTitleCardStyle('standard')}
                          >
                            Standard
                          </button>
                          <div
                            className="new-nle__style-hover-wrap"
                            onMouseEnter={() => setStyleHover('style1')}
                            onMouseLeave={() => setStyleHover(null)}
                          >
                            <button
                              type="button"
                              className={`new-nle__btn new-nle__btn--small${titleCardStyle === 'style1' ? ' new-nle__btn--on' : ''}`}
                              onClick={() => setTitleCardStyle('style1')}
                            >
                              Style 1
                            </button>
                            {styleHover === 'style1' && (
                              <div className="new-nle__style-tooltip">
                                <div className="new-nle__style-tooltip-title">Style 1 example</div>
                                <StyleHoverDemo
                                  styleId="style1"
                                  primary={titleFontColor || '#ffffff'}
                                  accent={secondColor || '#F5D66D'}
                                />
                                <div className="new-nle__style-tooltip-hint">Mixed fonts and alternating colors as lines appear.</div>
                              </div>
                            )}
                          </div>
                          <div
                            className="new-nle__style-hover-wrap"
                            onMouseEnter={() => setStyleHover('style2')}
                            onMouseLeave={() => setStyleHover(null)}
                          >
                            <button
                              type="button"
                              className={`new-nle__btn new-nle__btn--small${titleCardStyle === 'style2' ? ' new-nle__btn--on' : ''}`}
                              onClick={() => setTitleCardStyle('style2')}
                            >
                              Style 2
                            </button>
                            {styleHover === 'style2' && (
                              <div className="new-nle__style-tooltip">
                                <div className="new-nle__style-tooltip-title">Style 2 example</div>
                                <StyleHoverDemo
                                  styleId="style2"
                                  primary={titleFontColor || '#ffffff'}
                                  accent={secondColor || '#F5D66D'}
                                />
                                <div className="new-nle__style-tooltip-hint">One font and color; words reveal one-by-one in the center.</div>
                              </div>
                            )}
                          </div>
                          <div
                            className="new-nle__style-hover-wrap"
                            onMouseEnter={() => setStyleHover('style3')}
                            onMouseLeave={() => setStyleHover(null)}
                          >
                            <button
                              type="button"
                              className={`new-nle__btn new-nle__btn--small${titleCardStyle === 'style3' ? ' new-nle__btn--on' : ''}`}
                              onClick={() => setTitleCardStyle('style3')}
                            >
                              Style 3
                            </button>
                            {styleHover === 'style3' && (
                              <div className="new-nle__style-tooltip">
                                <div className="new-nle__style-tooltip-title">Style 3 example</div>
                                <StyleHoverDemo
                                  styleId="style3"
                                  primary={titleFontColor || '#ffffff'}
                                  accent={secondColor || '#F5D66D'}
                                />
                                <div className="new-nle__style-tooltip-hint">Same as Style 2, but lines appear whole; top line stays fixed.</div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {titleCardStyle === 'style1' && (
                        <div className="new-nle__subs-group">
                          <span className="new-nle__subs-label">2nd color</span>
                          <input
                            type="color"
                            className="new-nle__subs-color"
                            value={/^#[0-9a-fA-F]{6}$/.test(secondColor) ? secondColor : '#F5D66D'}
                            onChange={(e) => setSecondColor(e.target.value)}
                          />
                          <input
                            type="text"
                            className="new-nle__subs-hex"
                            value={secondColor}
                            onChange={(e) => setSecondColor(e.target.value)}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
                ) : (
                  <span className="new-nle__subs-hint">Available for Boukar only</span>
                )}
              </div>
            </div>
            </div>
          </div>
        )}

        {/* Music modal */}
        {showMusicModal && (
          <div className="new-nle-modal" onClick={closeMusicModal}>
            <div className="new-nle-modal__card" onClick={(e) => e.stopPropagation()}>
              <div className="new-nle-modal__head">
                <h3>Music</h3>
                <button type="button" className="new-nle__icon-btn" onClick={closeMusicModal}>
                  <X size={16} />
                </button>
              </div>
              <button
                type="button"
                className={`new-nle-modal__row${!useMusic ? ' new-nle-modal__row--on' : ''}`}
                onClick={() => { setUseMusic(false); setMusicId(''); closeMusicModal(); }}
              >
                No music
              </button>
              {(availableMusic || []).map((m) => (
                <div
                  key={m._id}
                  className={`new-nle-modal__row new-nle-modal__row--music${useMusic && String(musicId) === String(m._id) ? ' new-nle-modal__row--on' : ''}`}
                >
                  <button
                    type="button"
                    className="new-nle-modal__music-preview"
                    onClick={() => previewMusic(m)}
                    aria-label={previewMusicId === m._id ? 'Pause' : 'Play'}
                  >
                    {previewMusicId === m._id ? <Pause size={14} /> : <Play size={14} />}
                  </button>
                  <button
                    type="button"
                    className="new-nle-modal__music-select"
                    onClick={() => {
                      setUseMusic(true);
                      setMusicId(m._id);
                      closeMusicModal();
                    }}
                  >
                    {m.name || 'Untitled'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SFX modal */}
        {showSfxModal && (
          <div className="new-nle-modal" onClick={closeSfxModal}>
            <div className="new-nle-modal__card" onClick={(e) => e.stopPropagation()}>
              <div className="new-nle-modal__head">
                <h3>Add SFX @ {formatNleTime(sfxModalTime)}</h3>
                <button type="button" className="new-nle__icon-btn" onClick={closeSfxModal}>
                  <X size={16} />
                </button>
              </div>
              <div className="new-nle-modal__sfx-grid">
                {SFX_CATALOG.map((s) => (
                  <div key={s.id} className="new-nle-modal__sfx-item">
                    <button type="button" className="new-nle-modal__sfx-preview" onClick={() => previewSfx(s)}>
                      {previewSfxId === s.id ? <Pause size={12} /> : <Play size={12} />}
                    </button>
                    <button type="button" className="new-nle-modal__sfx-add" onClick={() => addSfxAt(s, sfxModalTime)}>
                      <strong>{s.label}</strong>
                      <span>{s.when}</span>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
